import { createWeb3Modal, useWeb3Modal } from '@web3modal/wagmi/react'
import { formatRelative, intervalToDuration, min } from 'date-fns'
import { cloneDeep, isEmpty, isEqual, toString, trim } from 'lodash'
import React, { CSSProperties, ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import styled, { createGlobalStyle, css, keyframes } from 'styled-components'
import {
  Address, createPublicClient, createWalletClient, custom, erc20Abi, getContract, maxUint256, parseEther, parseUnits
} from 'viem'
import { waitForTransactionReceipt } from 'viem/actions'
import { useAccount, useDisconnect } from 'wagmi'
import WaveSvg from '../animation/Wave.svg'
import WaveSvg2 from '../animation/Wave2.svg'
import WaveSvg3 from '../animation/Wave3.svg'
import Radom, {
  IDiscountCode, IManagedPaymentMethodDetails, IManagedPaymentStatus, IManagedTokenDelegatePaymentMethodDetails,
  InputFieldWithValue, PriceQuote, type Profile
} from '../api/Radom'
import Copy from '../icons/Copy'
import GasPump from '../icons/GasPump'
import { formatSubscriptionDuration, RADOM_COLORS } from '../util/Constants'
import { getMethod, isAutopayEnabled, isDelistedMethod, ManagedPaymentMethod, NetworkToChainMap, projectId, wagmiConfig } from '../util/Managed'
import { convertValue, getTotalSum } from '../util/Products'
import { generateQRCode } from '../util/QRCode'
import { PrimaryButton, SecondaryButton } from './Button'
import Checkbox from './Checkbox'
import {
  CheckoutData, TravelRuleFields, TravelRuleFieldsCollected, emptyTravelRuleFields,
  zeroTravelRuleFieldsCollected
} from './Checkout'
import { Cookie, InputLabel, TextInput, InlineInputError, INPUT_ERROR_STYLE } from './Input'
import SolanaWallet from './SolanaWallet'
import Spinner from './Spinner'
import TravelRuleFieldsWidget from './TravelRuleFieldsWidget'
import Modal from './Modal'
import { Chevron } from '../icons/Chevron'
import { InfoIcon } from '../icons/Info'
import LinkExternal from '../icons/LinkExternal'
import EthLogo from '../images/EthLogo.svg'
import { errorToast, resolveNavigationUrl, shortAddress, simpleEmailValidation } from '../util/Util'

const MANAGED_STEP_TRANSITION_DELAY_MS = 380
const CURRENCY_SEARCH_THRESHOLD = 7
const MOBILE_PAYMENT_BREAKPOINT = 900

export function formatSecondsToMinutesAndSecondsString(totalSeconds: number): string {
  const duration = intervalToDuration({ start: 0, end: totalSeconds * 1000 })

  const zeroPad = (num): string => String(num).padStart(2, '0')

  const formatted = [
    duration.days,
    duration.hours,
    duration.minutes,
    duration.seconds
  ]
    .filter((a, i) => i <= 1 ? !!a : true)
    .map(zeroPad)
    .join(':')

  return formatted
}

const formatPaymentAddressForDisplay = (address?: string): string => {
  if (!address) {
    return ''
  }

  const prefix = address.startsWith('0x') ? '0x' : ''
  const body = prefix ? address.slice(2) : address
  const chunkSize = body.length > 52 ? 18 : 16
  const parts = body.match(new RegExp(`.{1,${chunkSize}}`, 'g')) ?? [body]

  return `${prefix}${parts.join('\u200b')}`
}

const Styles = createGlobalStyle`
  @property --angle {
    syntax: '<angle>';
    initial-value: 90deg;
    inherits: true;
  }

  :root {
    --d: 2500ms;
    --c1: rgba(0, 0, 0, 0.5);
    --c2: rgba(0, 0, 0, 0.1);
  }
`

const ConnectedWalletContainer = styled.div`
  box-shadow: 0 0 3px ${RADOM_COLORS.GRAY_DARK};
  border-radius: 5px;
`

export const QRCodeWrapper = styled.div`
  border-radius: 10px;
  overflow: hidden;
  width: fit-content;
  box-shadow: 0 5px 5px ${RADOM_COLORS.GRAY_DARK};
  transition: 0.2s ease all;
  cursor: pointer;
  &:active {
    transform: scale(0.99);
  }
`

export const QRCodeWrapperInner = styled.div`
  width: fit-content;
  border-radius: 10px;
  box-shadow: 0 5px 5px ${RADOM_COLORS.GRAY_DARK};
  padding: 10px;
  font-size: 0;
`

export const WrapperIn = keyframes`
  from {
    transform: translateY(-25%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`

const WrapperAnimation = css`
  animation: ${WrapperIn} 0.26s cubic-bezier(0.22, 1, 0.36, 1);
`

const StepShellIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(12px) scale(0.992);
    filter: blur(4px);
  }
  65% {
    opacity: 1;
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
  }
`

const StepContentIn = keyframes`
  0% {
    opacity: 0;
    transform: translateX(10px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
`

const Wrapper = styled.div<{ disableAnimation?: boolean }>`
  flex-grow: 1;
  ${({ disableAnimation }) => !disableAnimation && WrapperAnimation}
`

const Container = styled.div<{ disableAnimation?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  ${({ disableAnimation }) => !disableAnimation && WrapperAnimation}
  // animation: ${WrapperIn} 0.2s ease;
`

const CHECKOUT_SPACING = {
  xs: 8,
  sm: 12,
  md: 16,
  lg: 24,
  xl: 32
} as const

const StepShell = styled.div<{ compact?: boolean }>`
  width: min(680px, 100%);
  margin: 0;
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ compact }) => compact ? '24px' : '16px'};
  transform-origin: 50% 16px;
  animation: ${StepShellIn} 0.38s cubic-bezier(0.22, 1, 0.36, 1);

  @media (max-width: 900px) {
    width: 100%;
    gap: ${({ compact }) => compact ? '20px' : '14px'};
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const StepTransitionFrame = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: ${StepContentIn} 0.28s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: opacity, transform;

  @media (max-width: 900px) {
    gap: 14px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const StepHeader = styled.div<{ compact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ compact }) => compact ? '16px' : '10px'};
`

const StepHeaderTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`

const StepEyebrow = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-size: 12px;
  font-weight: 600;
  color: #748199;
`

const StepTitle = styled.h2<{ compact?: boolean }>`
  margin: 0;
  font-size: ${({ compact }) => compact ? '20px' : '30px'};
  line-height: ${({ compact }) => compact ? '1.2' : '1.1'};
  font-weight: ${({ compact }) => compact ? 500 : 600};
  color: ${({ compact }) => compact ? '#172133' : '#111827'};
`

const StepTitleMeta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.58em;
  line-height: 1.15;
  font-weight: 500;
  color: #64748b;
  margin-left: 10px;
  white-space: nowrap;

  img {
    width: 18px;
    height: 18px;
    border-radius: 999px;
    flex-shrink: 0;
  }
`

const StepSubtitle = styled.p<{ compact?: boolean }>`
  margin: 0;
  font-size: ${({ compact }) => compact ? '12px' : '14px'};
  line-height: ${({ compact }) => compact ? '1.35' : '1.45'};
  color: ${({ compact }) => compact ? '#7b8699' : '#475569'};
`

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 19px;
  line-height: 1.25;
  font-weight: 600;
  color: #172033;
`

const SectionHelper = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  color: #546177;
`

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const FormFieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const SelectableList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #dbe3ef;
  border-radius: 14px;
  overflow: hidden;
  background: white;
`

const SelectorTools = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const SelectorSearchInput = styled(TextInput)`
  min-height: 42px;
  border-radius: 12px;
  border-color: #d8dee9;
  background: #ffffff;
  font-size: 15px;
  line-height: 1.3;
  padding: 10px 12px;
`

const SelectableRow = styled.button<{ selected?: boolean }>`
  width: 100%;
  border: 0;
  border-bottom: 1px solid #edf2f8;
  background: ${({ selected }) => selected ? '#f4f7ff' : '#ffffff'};
  padding: 16px 18px;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  cursor: pointer;
  position: relative;
  box-shadow: ${({ selected }) => selected ? 'inset 3px 0 0 #3f6bf5, inset 0 0 0 1px #c9d8ff' : 'none'};
  transition:
    background-color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.16s ease,
    border-color 0.18s ease;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: ${({ selected }) => selected ? '#f3f7ff' : '#f8faff'};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0) scale(0.995);
  }

  &:focus-visible {
    outline: 2px solid ${RADOM_COLORS.BLUE5};
    outline-offset: -2px;
  }
`

const SelectableRowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`

const SelectableRowIcon = styled.div<{ src?: string }>`
  width: 34px;
  height: 34px;
  border-radius: 17px;
  border: 1px solid #e3e8f1;
  background: ${({ src }) => src ? `center/contain no-repeat url(${src})` : '#eef2f9'};
  flex-shrink: 0;
`

const PaymentMethodIconRoot = styled.div<{ size: number }>`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  border-radius: ${({ size }) => size / 2}px;
  border: 1px solid #e3e8f1;
  background: #ffffff;
  position: relative;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
`

const PaymentMethodIconMain = styled.div<{ src?: string }>`
  position: absolute;
  inset: 4px;
  border-radius: 999px;
  background: ${({ src }) => src ? `center/contain no-repeat url(${src})` : '#eef2f9'};
`

const PaymentMethodIconBadge = styled.div<{ size: number }>`
  position: absolute;
  right: -4px;
  bottom: -3px;
  width: ${({ size }) => Math.round(size * 0.52)}px;
  height: ${({ size }) => Math.round(size * 0.52)}px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.14);
  display: flex;
  align-items: center;
  justify-content: center;
`

const PaymentMethodIconBadgeImage = styled.div<{ src?: string, size: number }>`
  width: ${({ size }) => Math.round(size * 0.34)}px;
  height: ${({ size }) => Math.round(size * 0.34)}px;
  border-radius: 999px;
  background: ${({ src }) => src ? `center/contain no-repeat url(${src})` : '#eef2f9'};
`

const SelectableRowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`

const SelectableRowTitle = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: #0f172a;
`

const SelectableRowMeta = styled.span`
  font-size: 13px;
  color: #64748b;
`

const SelectableRowValue = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
`

const Badge = styled.span`
  border-radius: 999px;
  padding: 2px 8px;
  border: 1px solid #d4dcec;
  background: #f5f8ff;
  color: #516286;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
`

const HeroAmount = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;

  @media (max-width: 900px) {
    justify-content: flex-start;
  }
`

const HeroAmountSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-end;
  width: 100%;
  text-align: right;

  @media (max-width: 900px) {
    align-items: flex-start;
    text-align: left;
  }
`

const HeroAmountHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
  width: 100%;

  @media (max-width: 900px) {
    justify-content: flex-start;
  }
`

const HeroAmountLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #7a879b;
`

const HeroAmountValue = styled.span`
  font-size: 34px;
  line-height: 1;
  font-weight: 600;
  color: #0f172a;

  @media (max-width: 1120px) and (min-width: 901px) {
    font-size: 30px;
  }
`

const HeroAmountTicker = styled.span`
  font-size: 16px;
  color: #64748b;
  font-weight: 500;
`

const QrHero = styled.button<{ clickable?: boolean }>`
  all: unset;
  width: fit-content;
  margin: 0 auto;
  position: relative;
  isolation: isolate;
  overflow: hidden;
  border-radius: 14px;
  border: 1px solid #dbe4f1;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
  background: white;
  padding: 8px;
  display: block;
  cursor: ${({ clickable }) => clickable ? 'zoom-in' : 'default'};
  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease,
    border-color 0.18s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at top right, rgba(59, 101, 240, 0.14), transparent 58%);
    opacity: 0;
    transition: opacity 0.18s ease;
    pointer-events: none;
  }

  ${({ clickable }) => clickable && css`
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 12px 28px rgba(15, 23, 42, 0.09);
      border-color: #d2ddf0;
    }

    &:hover::before,
    &:focus-visible::before {
      opacity: 1;
    }

    &:focus-visible {
      outline: none;
      box-shadow:
        0 0 0 3px rgba(97, 138, 250, 0.14),
        0 12px 28px rgba(15, 23, 42, 0.09);
    }
  `}
`

const QrHeroVisual = styled.span`
  position: relative;
  display: block;
`

const QrHeroImage = styled.img`
  width: 124px;
  height: 124px;

  @media (max-width: 1120px) and (min-width: 901px) {
    width: 108px;
    height: 108px;
  }

  @media (max-width: 900px) {
    width: 124px;
    height: 124px;
  }
`

const QrHeroHint = styled.span`
  position: absolute;
  right: 10px;
  bottom: 10px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.8);
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.18);
  color: white;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.01em;
  opacity: 0;
  transform: translateY(4px);
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
  pointer-events: none;

  svg {
    width: 11px;
    height: 11px;
    flex-shrink: 0;
    stroke: currentColor;
  }

  ${QrHero}:hover &,
  ${QrHero}:focus-visible & {
    opacity: 1;
    transform: translateY(0);
  }
`

const QrModalBody = styled.div`
  position: relative;
  overflow: hidden;
  padding: 6px 28px 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
`

const QrModalGlow = styled.div`
  position: absolute;
  top: 4px;
  width: 260px;
  height: 260px;
  border-radius: 999px;
  background: radial-gradient(circle, rgba(59, 101, 240, 0.18) 0%, rgba(59, 101, 240, 0.08) 38%, transparent 72%);
  filter: blur(10px);
  pointer-events: none;
`

const QrModalDetails = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`

const QrModalMetaPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid #d9e3f8;
  background: rgba(255, 255, 255, 0.92);
  color: #324765;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.06);

  img {
    width: 16px;
    height: 16px;
    border-radius: 999px;
    flex-shrink: 0;
  }
`

const QrModalAmountBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`

const QrModalAmountLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7b879a;
`

const QrModalAmount = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  color: #0f172a;
  font-size: 28px;
  line-height: 1;
  font-weight: 600;
`

const QrModalAmountTicker = styled.span`
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
`

const QrModalFrame = styled.div`
  width: fit-content;
  border-radius: 22px;
  border: 1px solid #dbe4f1;
  background: white;
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.08);
  padding: 14px;
`

const QrModalImage = styled.img`
  width: min(68vw, 360px);
  height: min(68vw, 360px);
  display: block;
`

const QrModalCaption = styled.p`
  margin: 0;
  max-width: 372px;
  text-align: center;
  color: #63738c;
  font-size: 14px;
  line-height: 1.55;
`

const QrDisclosureButton = styled.button<{ expanded: boolean }>`
  width: fit-content;
  margin: 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  padding: 0;
  color: #4f5f7b;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  svg {
    transition: transform 0.18s ease;
    transform: rotate(${({ expanded }) => expanded ? 180 : 90}deg);
  }
`

const StatusPill = styled.div`
  width: fit-content;
  align-self: center;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid #d8e4ff;
  background: #eef4ff;
  color: #3158c5;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`

const HeaderStatusPill = styled(StatusPill)`
  align-self: auto;
  margin-left: auto;
  padding: 5px 12px;
  gap: 7px;
  font-size: 10.25px;
  line-height: 1;
  color: #335dcb;
  background: #f6f8ff;
  border-color: #d8e2fb;
  box-shadow: none;

  @media (max-width: 900px) {
    margin-left: 0;
  }
`

const TimerPillIcon = styled.span`
  position: relative;
  width: 13px;
  height: 13px;
  border: 1.5px solid currentColor;
  border-radius: 999px;
  display: inline-block;
  flex-shrink: 0;
  opacity: 0.9;

  ::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 2px;
    width: 1.4px;
    height: 3.5px;
    background: currentColor;
    border-radius: 999px;
    transform: translateX(-50%);
  }

  ::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    width: 3px;
    height: 1.4px;
    background: currentColor;
    border-radius: 999px;
    transform: translateY(-50%);
    transform-origin: left center;
  }
`

const PaymentDetailsBlock = styled.div`
  border: 1px solid #dde4f0;
  border-radius: 14px;
  background: #fbfcff;
  padding: 13px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const PaymentDetailRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

const DetailLabel = styled.span`
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
`

const DetailValue = styled.span`
  font-size: 15px;
  color: #0f172a;
  font-weight: 500;
  text-align: right;
`

const MonoValue = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 13px;
  color: #1e293b;
  overflow-wrap: anywhere;
  text-align: right;
`

const PaymentAddressValue = styled.div`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 12px;
  line-height: 1.5;
  color: #1e293b;
  word-break: normal;
  overflow-wrap: anywhere;
  user-select: all;
  padding: 15px 74px 15px 18px;
  border-radius: 10px;
  border: 1px solid #e2e8f2;
  background: white;

  @media (max-width: 1120px) and (min-width: 901px) {
    font-size: 11.5px;
    padding: 14px 70px 14px 16px;
  }
`

const PaymentAddressField = styled.div`
  position: relative;
  width: 100%;
`

const PaymentFlowDivider = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #5d6d86;
  font-size: 13px;
  font-weight: 600;

  ::before,
  ::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e3e9f3;
  }
`

const ManualPaymentCard = styled.div`
  border: 1px solid #dde4f0;
  border-radius: 16px;
  background: #fbfcff;
  overflow: hidden;
`

const ManualPaymentGrid = styled.div<{ hasQr: boolean }>`
  display: grid;
  grid-template-columns: ${({ hasQr }) => hasQr ? '124px minmax(0, 1fr)' : '1fr'};
  align-items: start;
  gap: 28px;
  padding: 28px 28px 24px;

  @media (max-width: 1120px) and (min-width: 901px) {
    grid-template-columns: 108px minmax(0, 1fr);
    gap: 22px;
    padding: 24px 24px 22px;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 16px;
  }
`

const ManualPaymentInfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
  min-width: 0;
`

const ManualInfoDivider = styled.div`
  height: 1px;
  background: #e6ebf2;
`

const ManualPaymentAddressSection = styled.div`
  padding: 22px 26px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 1120px) and (min-width: 901px) {
    padding: 20px 24px 24px;
  }

  @media (max-width: 900px) {
    padding: 20px 16px 20px;
  }
`

const ManualNoticeIcon = styled(InfoIcon)`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  opacity: 0.55;
`

const ManualPaymentFooter = styled.div`
  border-top: 1px solid #e6ebf2;
  background: #f7f9fd;
  padding: 16px 26px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #5b6b83;
  font-size: 14px;
  line-height: 1.45;

  @media (max-width: 1120px) and (min-width: 901px) {
    padding: 14px 22px;
  }

  @media (max-width: 900px) {
    padding: 12px 16px;
  }
`

const UnderpaidNotice = styled.div`
  border: 1px solid #f0d5a1;
  border-radius: 14px;
  background: #fffaf0;
  overflow: hidden;
`

const UnderpaidNoticeHeader = styled.div`
  padding: 12px 16px;
  background: #fbeed4;
  color: #8a5a12;
  font-size: 13px;
  font-weight: 600;
`

const UnderpaidNoticeBody = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  color: #5b6b83;
  font-size: 14px;
  line-height: 1.45;
`

const CTASection = styled.div`
  border-top: 1px solid #e6ebf2;
  padding-top: 14px;
  padding-bottom: 8px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const WalletConnectSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  padding-bottom: 10px;
  width: 100%;
`

const PaymentHeroActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const LoadingStateGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 116px;
  padding: 12px 0 4px;
  animation: ${WrapperIn} 0.28s cubic-bezier(0.22, 1, 0.36, 1);
`

const PaymentStepColumn = styled.div`
  width: min(640px, 100%);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 18px;

  @media (max-width: 900px) {
    width: 100%;
    gap: 14px;
  }
`

const StatusTimeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const StatusTimelineRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const StatusDot = styled.span<{ state: 'done' | 'active' | 'pending' }>`
  width: 12px;
  height: 12px;
  border-radius: 6px;
  display: inline-block;
  flex-shrink: 0;
  background: ${({ state }) => state === 'done' ? '#22c55e' : state === 'active' ? '#3f6bf5' : '#cdd6e5'};
`

export const TokenSelector = styled.div<{ isSelected: boolean, isDisabled: boolean, hoverEffect?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 15px;
  border-radius: ${({ hoverEffect }) => hoverEffect ? '0px' : '5px'};
  cursor: pointer;
  border-bottom: 1px solid;
  ${({ isSelected }) => isSelected && 'border: 1px solid;'}
  border-color: ${({ isSelected }) => isSelected ? RADOM_COLORS.NICE_GREEN : RADOM_COLORS.GRAY_DARK};
  box-shadow: ${({ isSelected }) => isSelected && `0 0 3px ${RADOM_COLORS.NICE_GREEN}`};
  opacity: ${({ isSelected }) => isSelected ? '1' : '0.3'};
  transition: 0.2s ease all;
  user-select: none;

  ${({ isSelected }) => isSelected && `
    position: sticky;
    top: 0;
    bottom: 0;
    background: white;
    z-index: 1;
  `}

  ${({ isDisabled }) => isDisabled && 'opacity: 0.5;'}

  &:hover {
    opacity: ${({ isSelected }) => isSelected ? 1 : 0.6};
    ${({ hoverEffect }) => hoverEffect && `
      background-color: ${RADOM_COLORS.GRAY_LIGHTEST};
    `}
  }

  &:active {
    opacity: ${({ isSelected }) => isSelected ? 1 : 0.8};
    transform: scale(0.98);
  }
`

export const WaveContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  top: 0;
  z-index: -1;
  overflow: hidden;
`

const WaveAnim = keyframes`
  0% {
    margin-left: 0;
  }
  100% {
    margin-left: -1600px;
  }
`

export const Wave = styled.div`
  background: url(${WaveSvg as string}) repeat-x;
  position: absolute;
  width: 6400px;
  height: 50%;
  top: calc(50% - 100px);
  animation: ${WaveAnim} 18s cubic-bezier( 0.36, 0.45, 0.63, 0.53) infinite;
`

const WaveAnim2 = keyframes`
  0% {
    margin-left: 0;
  }
  100% {
    margin-left: -3200px;
  }
`

export const Wave2 = styled(Wave)`
  background: url(${WaveSvg2 as string}) repeat-x;
  animation: ${WaveAnim2} 15s cubic-bezier( 0.36, 0.45, 0.63, 0.53) infinite;
`
const WaveAnim3 = keyframes`
  0% {
    margin-left: -800px;
  }
  100% {
    margin-left: -2400px;
  }
`

export const Wave3 = styled(Wave)`
  background: url(${WaveSvg3 as string}) repeat-x;
  animation: ${WaveAnim3} 6s cubic-bezier( 0.36, 0.45, 0.63, 0.53) infinite;
`

const Expand = keyframes`
  0% {
    transform: scaleY(0%);
  }
  100% {
    transform: scaleY(100%);
  }
`

const PaymentDetectedContainer = styled.div`
  animation: ${Expand} 0.2s cubic-bezier(0, 1, 0, 1);
  transform-origin: 0 0;
  border-radius: 15px;
  border: 1px solid ${RADOM_COLORS.GRAY_MED};
  overflow: hidden;
`

const rotate = keyframes`
  100% { transform: rotate(1turn); }
`

const LoaderWrapper = styled.div`
  width: 50px;
  aspect-ratio: 1;
  padding: 2px;
  border-radius: 50%;
  opacity: 0.8;
  background:
    radial-gradient(farthest-side, ${RADOM_COLORS.BLUE5} 94%, #0000) top/8px 8px no-repeat,
    conic-gradient(#0000 30%, ${RADOM_COLORS.INFO});
  -webkit-mask: radial-gradient(farthest-side, #0000 calc(100% - 8px), #000 0);
  animation: ${rotate} 1s infinite linear;
`

interface Props {
  onClick: () => void
  style?: CSSProperties
  iconColor?: string
}

export const CopyButton = ({ onClick, style, iconColor = RADOM_COLORS.GRAY_DARKEST }: Props): ReactElement => {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (copied) {
      const timeout = setTimeout(() => setCopied(false), 3000)
      return () => clearTimeout(timeout)
    }
  }, [copied])

  return <SecondaryButton
    onClick={() => { setCopied(true); onClick() }}
    style={{
      padding: '2px 4px',
      fontSize: 10,
      textTransform: 'uppercase',
      background: RADOM_COLORS.GRAY_MED,
      color: RADOM_COLORS.GRAY_DARKEST,
      cursor: 'pointer',
      borderRadius: 5,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      border: 'none',
      ...style
    }}>
    <span>{copied ? 'Copied' : 'Copy'}</span>
    <Copy style={{ height: 10 }} stroke={iconColor} />
  </SecondaryButton>
}

const logoKey = (logo: unknown): string => typeof logo === 'string' ? logo : String(logo ?? '')

const getNativeCurrencyLogo = (method: ManagedPaymentMethod): string | undefined => {
  if (method.ticker === 'ETH') {
    return EthLogo
  }

  return method.logo ?? method.tokenInfo?.logo
}

const getNetworkLogoForMethod = (method: ManagedPaymentMethod): string | undefined => {
  if (method.tokenInfo?.logo) {
    return method.tokenInfo.logo
  }

  const networkName = method.tokenInfo?.networkName ?? method.name
  try {
    return getMethod(networkName).logo
  } catch {
    return method.logo
  }
}

const PaymentMethodIcon = ({
  method,
  size = 34
}: {
  method: ManagedPaymentMethod
  size?: number
}): ReactElement => {
  const primaryLogo = getNativeCurrencyLogo(method)
  const networkLogo = getNetworkLogoForMethod(method)
  const showNetworkBadge = Boolean(
    networkLogo &&
    primaryLogo &&
    logoKey(networkLogo) !== logoKey(primaryLogo)
  )

  return (
    <PaymentMethodIconRoot size={size}>
      <PaymentMethodIconMain src={primaryLogo} />
      {showNetworkBadge && (
        <PaymentMethodIconBadge size={size}>
          <PaymentMethodIconBadgeImage src={networkLogo} size={size} />
        </PaymentMethodIconBadge>
      )}
    </PaymentMethodIconRoot>
  )
}

export interface CheckoutParams {
  freeInput?: {
    amount: number
    currency: string
  }
  managed?: {
    selectedMethod: ManagedPaymentMethod
  }
  completeTokenDelegate?: {
    paymentId: string
    signature: string
  }
  priceQuotes: PriceQuote[]
  subscriptionOptions?: {
    emailInvoice?: {
      subscriptionType: SubscriptionType.EmailInvoice
      emailAddress: string

    }
    autopay?: {
      subscriptionType: SubscriptionType.Autopay
      address: string
    }
  }
  travelRuleFields?: TravelRuleFields
}

interface IProps {
  checkoutData: CheckoutData
  currency: string
  isPreProcessingPayment: boolean
  isSuccess: boolean
  managedPaymentDetails?: IManagedPaymentMethodDetails
  tokenDelegatePaymentDetails?: IManagedTokenDelegatePaymentMethodDetails
  successElement: React.ReactNode
  inputsElement?: React.ReactNode
  errorMessage?: string
  discount?: IDiscountCode
  onCheckout: (params: CheckoutParams) => void | Promise<void>
  onQuoteUpdate: () => void
  posMode?: boolean
  onInputFieldsChange?: (inputs: InputFieldWithValue[]) => void
  disableAnimation?: boolean
  paymentStatus?: IManagedPaymentStatus

  // A set of flags to indicate which travel rule fields have already been collected
  travelRuleFieldsCollected?: TravelRuleFieldsCollected

  // Travel rule fields should be hidden when previewing the transaction
  hideTravelRuleFields?: boolean
  onTravelRuleFieldsContinue?: () => void
  onCheckoutStateChange?: (isCheckoutState: boolean) => void
  onChangePaymentMethod?: () => void
}

export enum CheckoutState {
  CollectTravelRuleFields,
  Checkout,
}

enum SubscriptionCheckoutState {
  Select,
  Confirm,
  Allowance,
  EmailInvoicePay
}

export enum SubscriptionType {
  EmailInvoice,
  Autopay
}

const DEFAULT_FIAT_CURRENCY = 'USD'

const inputFieldValidationError = (field: InputFieldWithValue): string | undefined => {
  if (!field.isRequired) {
    return undefined
  }

  const label = field.inputLabel.trim().length > 0 ? field.inputLabel.trim() : 'this field'

  if (typeof field.value === 'boolean') {
    return field.value ? undefined : `Select ${label}.`
  }

  if (typeof field.value === 'number') {
    return Number.isNaN(field.value) ? `Enter ${label}.` : undefined
  }

  const value = String(field.value ?? '').trim()
  if (value.length === 0) {
    return `Enter ${label}.`
  }

  if (field.dataType === 'Email' && !simpleEmailValidation(value)) {
    return 'Enter a valid email address.'
  }

  return undefined
}

const hasMissingRequiredInputValues = (fields: InputFieldWithValue[]): boolean => {
  return fields.some((field) => inputFieldValidationError(field) !== undefined)
}

export const determineTrfCollectionState = (trfFlagObj: TravelRuleFieldsCollected): CheckoutState => {
  const {
    accountType,
    companyNameCollected,
    addressLine1Collected,
    townOrCityCollected,
    ...otherFields
  } = trfFlagObj

  // Keep required address inputs minimal to reduce checkout friction while
  // still collecting the core travel-rule address fields.
  const addressMissing = !addressLine1Collected || !townOrCityCollected

  const needsCompany = accountType !== 'Individual' && !companyNameCollected

  const needsOther = Object.entries(otherFields).some(([key, value]) => key !== 'accountType' && !value)

  return addressMissing || needsCompany || needsOther
    ? CheckoutState.CollectTravelRuleFields
    : CheckoutState.Checkout
}

// ---------------------------------------------------------------------------------------------------------------------
// This widget is used by multiple transaction types.
// Each has a different way of handling the collection of travel rule field (TRF) information
//
// * Payment Links      Multiple Use  TRFs collected at checkout
// * Donation Links     Multiple Use  TRFs collected at checkout
// * Invoices           Single Use    Zero or more TRFs created by merchant. Remaining TRFs collected at checkout
// * Payment Sessions   Single Use    Zero or more TRFs created by merchant. Remaining TRFs collected at checkout
// * Checkout Sessions  Single Use    Zero or more TRFs created by merchant. Remaining TRFs collected at checkout
// ---------------------------------------------------------------------------------------------------------------------
const PayWidget = (props: IProps): React.ReactElement => {
  const [localCheckoutData, setLocalCheckoutData] = useState({} as any)

  const [selectedMethod, setSelectedMethod] = useState<ManagedPaymentMethod>()
  const [selectedTicker, setSelectedTicker] = useState<string>()
  const [selectedChainName, setSelectedChainName] = useState<string>()
  const [selectedTokenImageUrl, setSelectedTokenImageUrl] = useState<string>()
  const [isLoading, setIsLoading] = useState(false)
  const [isStepTransitioning, setIsStepTransitioning] = useState(false)
  const [managedRouteError, setManagedRouteError] = useState('')
  const [currencySearch, setCurrencySearch] = useState('')
  const [isMobilePaymentViewport, setIsMobilePaymentViewport] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_PAYMENT_BREAKPOINT : false
  )
  const [showMobileQr, setShowMobileQr] = useState(false)

  const [priceQuotes, setPriceQuotes] = useState<PriceQuote[]>([])
  const [quoteExpiryTimestamp, setQuoteExpiryTimestamp] = useState<Date>(new Date())
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [quoteSecondsRemaining, setQuoteSecondsRemaining] = useState(0)
  const [isLoadingPriceQuotes, setIsLoadingPriceQuotes] = useState(false)

  const { disconnect } = useDisconnect()
  const { address, isConnected, connector } = useAccount()

  const isOpenBanking = props.checkoutData?.gateway?.openBanking !== undefined

  if (typeof window !== 'undefined' && !(window as any).__W3M_INITIALIZED__ && !isOpenBanking && props.checkoutData) {
    createWeb3Modal({
      wagmiConfig,
      projectId
    })
    ;(window as any).__W3M_INITIALIZED__ = true
  }

  const { open } = useWeb3Modal()

  const [localInputFields, setLocalInputFields] = useState([] as InputFieldWithValue[])
  const [inputFieldsConfirmed, setInputFieldsConfirmed] = useState(true)
  const [inputFieldValidationAttempted, setInputFieldValidationAttempted] = useState(false)
  const [subscriptionValidationError, setSubscriptionValidationError] = useState('')
  const inputFieldsSourceRef = useRef('')
  const inputFieldsDefinitionKey = useMemo(() => {
    return (props.checkoutData.inputFields ?? [])
      .map(inputField => [
        inputField.inputLabel,
        inputField.dataType,
        inputField.isRequired ? 'required' : 'optional'
      ].join(':'))
      .join('|')
  }, [props.checkoutData.inputFields])

  const travelRulePrefilledData = useMemo<Partial<Profile>>(() => {
    const getInputValue = (label: string): string | undefined => {
      const field = localInputFields.find(
        inputField => inputField.inputLabel.trim().toLowerCase() === label
      )

      if (!field || typeof field.value !== 'string') {
        return undefined
      }

      const value = field.value.trim()
      return value.length > 0 ? value : undefined
    }

    const emailFromCheckoutData = props.checkoutData.travelRuleEmail?.trim()
    const email = (emailFromCheckoutData && emailFromCheckoutData.length > 0)
      ? emailFromCheckoutData
      : getInputValue('email address')
    const name = getInputValue('name') ?? getInputValue('representative name')
    const companyName = getInputValue('company name') ?? getInputValue('organization')
    const residenceCountry = getInputValue('country') ?? getInputValue('country of residence')
    const billingAddress = getInputValue('billing address') ?? getInputValue('address')

    return {
      email,
      name,
      companyName,
      residenceCountry,
      billingAddress
    }
  }, [localInputFields, props.checkoutData.travelRuleEmail])
  const [checkoutState, setCheckoutState] = useState<CheckoutState>(
    props.hideTravelRuleFields === true || props.checkoutData.isInvoice
      ? CheckoutState.Checkout
      : props.travelRuleFieldsCollected
        ? determineTrfCollectionState(props.travelRuleFieldsCollected)
        : CheckoutState.CollectTravelRuleFields
  )
  const [travelRuleFields, setTravelRuleFields] = useState(emptyTravelRuleFields())

  const inputRefs = useRef<Array<HTMLInputElement | null>>([])
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [subscriptionEmailAddress, setSubscriptionEmailAddress] = useState('')

  useEffect(() => {
    props.onCheckoutStateChange?.(checkoutState === CheckoutState.Checkout)
  }, [checkoutState, props.onCheckoutStateChange])

  useEffect(() => {
    if (props.managedPaymentDetails ?? props.tokenDelegatePaymentDetails) {
      setCheckoutState(CheckoutState.Checkout)
    }
  }, [props.managedPaymentDetails, props.tokenDelegatePaymentDetails])

  useEffect(() => {
    if (typeof window === 'undefined' || (window as any).__W3M_INITIALIZED__) {
      return
    }

    if (props.checkoutData && !props.checkoutData.gateway?.openBanking) {
      createWeb3Modal({
        wagmiConfig,
        projectId
      })
      ;(window as any).__W3M_INITIALIZED__ = true
    }
  }, [props.checkoutData])

  function formatCrypto8(value: number | string): string {
    const maxDp = 8
    const v = Number(value)
    if (!Number.isFinite(v)) return '—'

    const threshold = Math.pow(10, -maxDp)
    if (v > 0 && v < threshold) {
      return `< ${'0.' + '0'.repeat(maxDp - 1)}1`
    }
    const fixed = v.toFixed(maxDp)
    const trimmedZeros = fixed.replace(/(\.\d*?[1-9])0+$/, '$1')
      .replace(/\.0+$/, '')
    return trimmedZeros
  }

  useEffect(() => {
    const nextInputFields = props.checkoutData.inputFields?.map(inputField => ({ ...inputField })) ?? []
    const sourceKey = `${props.checkoutData.id ?? ''}:${inputFieldsDefinitionKey}`

    inputRefs.current = inputRefs.current.slice(0, nextInputFields.length)
    setLocalInputFields(nextInputFields)

    if (inputFieldsSourceRef.current !== sourceKey) {
      inputFieldsSourceRef.current = sourceKey
      setInputFieldsConfirmed(!hasMissingRequiredInputValues(nextInputFields))
      setInputFieldValidationAttempted(false)
    }
  }, [inputFieldsDefinitionKey, props.checkoutData.id, props.checkoutData.inputFields])

  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const updateViewportState = (): void => {
      setIsMobilePaymentViewport(window.innerWidth <= MOBILE_PAYMENT_BREAKPOINT)
    }

    updateViewportState()
    window.addEventListener('resize', updateViewportState)
    return () => window.removeEventListener('resize', updateViewportState)
  }, [])

  useEffect(() => {
    if (!isMobilePaymentViewport) {
      setShowMobileQr(true)
      return
    }

    setShowMobileQr(false)
  }, [isMobilePaymentViewport, props.managedPaymentDetails?.paymentId])

  // -------------------------------------------------------------------------------------------------------------------
  const getPriceQuotes = (): void => {
    props.onQuoteUpdate()
    setIsLoadingPriceQuotes(true)

    const fromCurList = [
      ...(props.checkoutData.products?.map(p => p.product.currency) ?? []),
      ...(props.checkoutData.lineItems?.map(p => p.currency ?? DEFAULT_FIAT_CURRENCY) ?? []),
      props.currency || DEFAULT_FIAT_CURRENCY
    ]
    const { managed } = props.checkoutData.gateway
    if (managed) {
      fromCurList.push(
        DEFAULT_FIAT_CURRENCY,
        ...managed.methods.filter(t => !isDelistedMethod(t.network, t.token)).map(t => getMethod(t.network, t.token).ticker || '')
      )
    }
    const fromCur = [...new Set(fromCurList)]

    if (fromCur.length === 0) {
      setIsLoadingPriceQuotes(false)
      return
    }

    Radom
      .getPriceQuotes(fromCur, fromCur)
      .then(priceQuotes => {
        const minTimestamp = min(priceQuotes.map(p => new Date(p.expiryTimestamp)))
        setQuoteExpiryTimestamp(minTimestamp)
        setQuoteSecondsRemaining((minTimestamp.getTime() - new Date().getTime()) / 1000)
        setPriceQuotes(priceQuotes)
      })
      .catch(err => {
        console.error('Failed to refresh pay widget quotes', err)
      })
      .finally(() => {
        setIsLoadingPriceQuotes(false)
      })
  }

  // -------------------------------------------------------------------------------------------------------------------
  const [totalSum, recurringSum] = useMemo(() => {
    return getTotalSum(props.checkoutData, priceQuotes, props.currency, props.discount)
  }, [props.checkoutData, priceQuotes, props.currency, props.discount])

  // -------------------------------------------------------------------------------------------------------------------
  const recurringInterval = (): number => {
    if (props.checkoutData.products) {
      const g = props.checkoutData.products.find(p => p.product.chargingIntervalSeconds > 0)
      if (g) {
        return g.product.chargingIntervalSeconds / 60 / 60 / 24
      }
    }

    if (props.checkoutData.lineItems) {
      const g = props.checkoutData.lineItems.find(p => p.chargingIntervalSeconds > 0)
      if (g) {
        return g.chargingIntervalSeconds / 60 / 60 / 24
      }
    }

    return 0
  }

  // -------------------------------------------------------------------------------------------------------------------
  const nextBillingAt = (): string => {
    if (props.checkoutData.products) {
      const g = props.checkoutData.products.find(p => p.product.chargingIntervalSeconds > 0)
      if (g?.product.nextBillingDateAt) {
        return formatRelative(new Date(g.product.nextBillingDateAt), new Date())
      }
    }

    if (props.checkoutData.lineItems) {
      const g = props.checkoutData.lineItems.find(p => p.chargingIntervalSeconds > 0)
      if (g?.nextBillingDateAt) {
        return formatRelative(new Date(g.nextBillingDateAt), new Date())
      }
    }

    return formatRelative(
      new Date(new Date().getTime() + recurringInterval() * 60 * 60 * 24 * 1000),
      new Date()
    )
  }

  // -------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (
      isEqual(localCheckoutData.gateway, props.checkoutData.gateway) &&
      isEqual(localCheckoutData.products, props.checkoutData.products) &&
      isEqual(localCheckoutData.lineItems, props.checkoutData.lineItems) &&
      isEqual(localCheckoutData.currency, props.checkoutData.currency) &&
      isEqual(localCheckoutData.total, props.checkoutData.total)
    ) {
      return
    }

    if (props.checkoutData.gateway.managed) {
      setLocalCheckoutData(cloneDeep(props.checkoutData))
      getPriceQuotes()
    }
  }, [props.checkoutData])

  useEffect(() => getPriceQuotes(), [props.checkoutData.products?.length])

  // -------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    const myInterval = setInterval(() => {
      if (quoteExpiryTimestamp.getTime() <= new Date().getTime()) {
        getPriceQuotes()
      }

      setQuoteSecondsRemaining((quoteExpiryTimestamp.getTime() - new Date().getTime()) / 1000)
    }, 1000)

    return () => clearInterval(myInterval)
  }, [quoteExpiryTimestamp])

  // -------------------------------------------------------------------------------------------------------------------
  const [managedPaymentQrCode, setManagedPaymentQrCode] = useState('')
  const [isDesktopQrModalVisible, setIsDesktopQrModalVisible] = useState(false)
  const [isManagedEVMTxLoading, setIsManagedEVMTxLoading] = useState(false)
  const [isSigningMessage, setIsSigningMessage] = useState(false)
  const [isSettingAllowance, setIsSettingAllowance] = useState(false)
  const [allowSkipAllowance, setAllowSkipAllowance] = useState(false)
  const [gasCost, setGasCost] = useState('')
  const [isLoadingAllowance, setIsLoadingAllowance] = useState(false)

  const [managedSubscriptionState, setManagedSubscriptionState] = useState<SubscriptionCheckoutState>()
  const [selectedSubscriptionType, setSelectedSubscriptionType] = useState<SubscriptionType>()
  const [receiveEmailNotifications, setReceiveEmailNotifications] = useState(false)

  useEffect(() => {
    if (isMobilePaymentViewport || !managedPaymentQrCode) {
      setIsDesktopQrModalVisible(false)
    }
  }, [isMobilePaymentViewport, managedPaymentQrCode, props.managedPaymentDetails?.paymentId])

  const onKeyboardSelect = (e: React.KeyboardEvent, callback: () => void): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      callback()
    }
  }

  const isBitcoinNetwork = useMemo(() => props.managedPaymentDetails?.network === 'Bitcoin', [props.managedPaymentDetails?.network])

  const {
    paymentStatus,
    pendingAmount,
    confirmedAmount,
    amountRemaining,
    hasPaymentDetected,
    hasConfirmedPayment,
    isUnderReview,
    isAwaitingConfirmation,
    isUnderpaid,
    amountLeft
  } = useMemo(() => {
    const status = props.paymentStatus?.status ?? 'pending'
    const pending = props.paymentStatus?.pendingTransactions.reduce((sum, tx) => sum + Number(tx.amount), 0) ?? 0
    const confirmed = props.paymentStatus?.transactions.reduce((sum, tx) => sum + Number(tx.amount), 0) ?? 0
    const remaining = Number(props.paymentStatus?.amountRemaining ?? 0)
    const underReview = status === 'inReview'
    const awaitingConfirmation = status === 'paymentDetected' && confirmed > 0 && remaining <= 0
    const isConfirmed = status === 'paymentConfirmed' ||
      (!underReview && !awaitingConfirmation && confirmed > 0 && remaining <= 0)
    const underpaid = !isConfirmed && !underReview && confirmed > 0 && remaining > 0

    return {
      paymentStatus: status,
      pendingAmount: pending,
      confirmedAmount: confirmed,
      amountRemaining: remaining,
      hasPaymentDetected: status === 'paymentDetected' || pending > 0,
      hasConfirmedPayment: isConfirmed,
      isUnderReview: underReview,
      isAwaitingConfirmation: awaitingConfirmation,
      isUnderpaid: underpaid,
      amountLeft: underpaid
        ? props.paymentStatus?.amountRemaining ?? ''
        : props.managedPaymentDetails?.amount ?? ''
    }
  }, [props.paymentStatus, props.managedPaymentDetails?.amount])

  const amountLeftPaymentDetails = useMemo(
    () => props.managedPaymentDetails
      ? { ...props.managedPaymentDetails, amount: amountLeft }
      : undefined,
    [props.managedPaymentDetails, amountLeft]
  )

  const generateManagedPaymentQrCode = async (): Promise<void> => {
    if (!amountLeftPaymentDetails) {
      return
    }

    const [qrCodeData] = await generateQRCode(amountLeftPaymentDetails)
    setManagedPaymentQrCode(qrCodeData)
  }

  // -------------------------------------------------------------------------------------------------------------------
  const onClickOpenWallet = async (): Promise<void> => {
    if (!amountLeftPaymentDetails) {
      return
    }

    switch (amountLeftPaymentDetails.network) {
    case 'Bitcoin':
    case 'Solana':
    case 'Polkadot':
    case 'PolkadotTestnet':
    case 'PolkadotAssetHub':
    case 'PolkadotAssetHubTestnet':
      break
    case 'Ethereum':
    case 'SepoliaTestnet':
    case 'Polygon':
    case 'PolygonTestnet':
    case 'BNB':
    case 'BNBTestnet':
    case 'Base':
    case 'Optimism':
    case 'OpBnb':
    case 'Arbitrum':
    case 'Avalanche':
      try {
        if (!address) {
          await open()
          return
        }

        setIsManagedEVMTxLoading(true)

        const chain = NetworkToChainMap[amountLeftPaymentDetails.network] as any
        const provider: any = await connector?.getProvider()

        const signer = createWalletClient({
          chain,
          transport: custom(provider)
        })

        if (connector?.switchChain) {
          const chainId = await connector.getChainId()
          if (chainId !== chain.id) {
            await connector?.switchChain({ chainId: chain.id })
          }
        }

        if (amountLeftPaymentDetails.token) {
          const method = getMethod(amountLeftPaymentDetails.network, amountLeftPaymentDetails.token)

          const contract = getContract({
            address: amountLeftPaymentDetails.token as any,
            abi: erc20Abi,
            client: signer
          })

          try {
            const balance = await contract.read.balanceOf([address])
            const decimals = await contract.read.decimals()
            const parsedAmount = parseUnits(amountLeftPaymentDetails.amount, decimals)
            if (balance < parsedAmount) {
              setIsManagedEVMTxLoading(false)
              errorToast(`Your wallet does not have enough ${selectedMethod?.name} for payment.`)
              return
            }
          } catch (err) {
            console.error('Failed to get token balance using injected provider', err)
          }

          await contract.write.transfer([
            amountLeftPaymentDetails.paymentAddress as Address,
            parseUnits(amountLeftPaymentDetails.amount, method.tokenInfo?.decimals ?? 6)
          ], { account: address, chain })
        } else {
          const client = createPublicClient({
            chain,
            transport: custom(provider)
          })

          try {
            const balance = await client.getBalance({ address })
            if (balance < parseEther(amountLeftPaymentDetails.amount)) {
              setIsManagedEVMTxLoading(false)
              errorToast(`Your wallet does not have enough ${selectedMethod?.ticker ?? 'amount'} for payment.`)
              return
            }
          } catch (err) {
            console.error('Failed to get balance using injected provider', err)
          }

          if (!signer) return

          await signer.sendTransaction({
            account: address,
            chain,
            to: amountLeftPaymentDetails.paymentAddress as Address,
            value: parseEther(amountLeftPaymentDetails.amount)
          })
        }
      } catch (err) {
        setIsManagedEVMTxLoading(false)
        errorToast('Failed to approve payment. Please ensure you have enough funds.')
        console.error(err)
      }
      break
    default:
      console.error('No wallet')
    }
  }

  // -------------------------------------------------------------------------------------------------------------------
  const setManagedSubscriptionAllowance = async (): Promise<void> => {
    if (selectedMethod?.tokenInfo?.tokenType !== 'evm') {
      return
    }

    if (!address) {
      await open()
      return
    }

    setIsSettingAllowance(true)

    try {
      if (!address) return

      const chain = NetworkToChainMap[selectedMethod.tokenInfo.networkName] as any
      const provider: any = await connector?.getProvider()

      const signer = createWalletClient({
        chain,
        transport: custom(provider)
      })

      if (connector?.switchChain) {
        const chainId = await connector.getChainId()
        if (chainId !== chain.id) {
          await connector?.switchChain({ chainId: chain.id })
        }
      }

      const contract = getContract({
        address: selectedMethod.tokenInfo.tokenAddress as any,
        abi: erc20Abi,
        client: { public: signer, wallet: signer }
      })
      const balance = await contract.read.balanceOf([address])

      const parsedAmount = parseUnits(totalSum.toString(), selectedMethod.tokenInfo.decimals)
      if (balance < parsedAmount) {
        setIsSettingAllowance(false)
        errorToast(`Your wallet does not have enough ${selectedMethod?.name} for payment.`)
        return
      }

      const allowance = await contract.read.allowance([
        address,
        selectedMethod.tokenInfo.subscriptionContract as Address
      ])

      if (allowance <= BigInt(0) || allowance < parsedAmount * BigInt(300)) {
        if (selectedMethod.name === 'Tether' && allowance > 0) {
          const hash = await contract.write.approve(
            [selectedMethod.tokenInfo.subscriptionContract as any, 0n],
            {
              account: address as any,
              chain
            }
          )

          if (hash) {
            try {
              await waitForTransactionReceipt(signer, {
                hash,
                retryCount: 30,
                retryDelay: 1000
              })
            } catch (err) {
              console.error('Failed to confirm allowance tx', err)
            }
          }
        }

        const hash = await contract.write.approve(
          [selectedMethod.tokenInfo.subscriptionContract as any, maxUint256],
          {
            account: address as any,
            chain
          }
        )

        if (hash) {
          try {
            console.log('Waiting for tx receipt')
            await waitForTransactionReceipt(signer, {
              hash,
              retryCount: 30,
              retryDelay: 1000
            })
          } catch (err) {
            console.error('Failed to confirm allowance tx', err)
          }
        }
      }

      setIsSettingAllowance(false)
      setManagedSubscriptionState(SubscriptionCheckoutState.Confirm)
    } catch (err) {
      setIsSettingAllowance(false)
      errorToast('Failed to set allowance')
      console.error(err)
    }
  }

  // -------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (props.managedPaymentDetails) {
      generateManagedPaymentQrCode()
    }
  }, [props.managedPaymentDetails, amountLeft])

  // -------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (props.managedPaymentDetails) {
      if (managedSubscriptionState !== undefined) {
        setManagedSubscriptionState(SubscriptionCheckoutState.EmailInvoicePay)
      }
    }
  }, [props.managedPaymentDetails])

  // -------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (managedSubscriptionState === undefined) {
      setSelectedSubscriptionType(undefined)
      setReceiveEmailNotifications(false)
    }

    if (selectedMethod?.tokenInfo && selectedMethod.tokenInfo.tokenType === 'evm') {
      Radom.estimateTokenTranferGas(selectedMethod.tokenInfo.networkName, selectedMethod.tokenInfo.tokenAddress)
        .then(g => {
          setGasCost(g.gasCost)
        })
        .catch(err => console.error(err))
    }
  }, [managedSubscriptionState])

  // -------------------------------------------------------------------------------------------------------------------
  const computeAllowanceStep = async (): Promise<void> => {
    if (!props.tokenDelegatePaymentDetails) {
      return
    }

    if (selectedMethod?.tokenInfo?.tokenType !== 'evm') {
      return
    }

    if (!address || !connector) {
      await open()
      return
    }

    setIsLoadingAllowance(true)

    let signer
    const chain = NetworkToChainMap[props.tokenDelegatePaymentDetails.network] as any

    try {
      const provider: any = await connector?.getProvider()

      signer = createWalletClient({
        chain,
        transport: custom(provider)
      })

      if (connector?.switchChain) {
        const chainId = await connector.getChainId()
        if (chainId !== chain.id) {
          await connector?.switchChain({ chainId: chain.id })
        }
      }
    } catch (err) {
      setIsLoadingAllowance(false)
      errorToast(err.toString())
      return
    }

    const contract = getContract({
      address: selectedMethod.tokenInfo.tokenAddress as any,
      abi: erc20Abi,
      client: { public: signer, wallet: signer }
    })

    try {
      if (!address) return
      const allowance = await (contract as any).read.allowance([
        address,
        selectedMethod.tokenInfo.subscriptionContract as Address
      ])
      const parsedAmount = parseUnits(totalSum.toString(), 6)
      if (allowance >= parsedAmount) {
        setAllowSkipAllowance(true)
      }
    } catch (err) {
      setAllowSkipAllowance(true)
    }

    setIsLoadingAllowance(false)
    setManagedSubscriptionState(SubscriptionCheckoutState.Allowance)
  }

  // -------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (props.tokenDelegatePaymentDetails) {
      computeAllowanceStep()
    } else if (managedSubscriptionState === SubscriptionCheckoutState.Confirm) {
      setManagedSubscriptionState(SubscriptionCheckoutState.Select)
    }
  }, [props.tokenDelegatePaymentDetails])

  // -------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (!props.errorMessage) return
    errorToast(props.errorMessage)
  }, [props.errorMessage])

  // -------------------------------------------------------------------------------------------------------------------
  // If any or all of the travel rule fields are missing, collect these first
  const trfWidget = !props.checkoutData.isInvoice && checkoutState === CheckoutState.CollectTravelRuleFields
    ? <TravelRuleFieldsWidget
      travelRuleFieldsCollected={
        props.travelRuleFieldsCollected ?? zeroTravelRuleFieldsCollected()
      }
      onTravelRuleFieldsComplete={async collectedTrFields => {
        props.onTravelRuleFieldsContinue?.()
        setCheckoutState(CheckoutState.Checkout)
        setTravelRuleFields(collectedTrFields)

        const checkoutDataId = trim(toString(props.checkoutData?.id))

        if (!isEmpty(checkoutDataId)) {
          try {
            if (props.checkoutData.isInvoice) {
              await Radom.updateTrfsForCustomerViaInvoice(checkoutDataId, collectedTrFields)
            } else if (props.checkoutData.sessionStatus !== undefined) {
              await Radom.upsertTrfsForCheckoutSession(checkoutDataId, collectedTrFields)
            }
          } catch (err) {
            console.error('Failed to save travel rule fields', err)
          }
        }

        // If matching custom fields are still blank, seed them from the latest travel-rule values.
        let inputFieldsUpdated = false
        const newLocalInputFields = localInputFields.map(inputField => {
          const normalizedLabel = inputField.inputLabel.trim().toLowerCase()
          const currentValue = String(inputField.value ?? '').trim()

          if (normalizedLabel === 'email address' && currentValue.length === 0 && collectedTrFields.emailAddress) {
            inputFieldsUpdated = true
            return { ...inputField, value: collectedTrFields.emailAddress }
          }

          if (normalizedLabel === 'name' && currentValue.length === 0 && collectedTrFields.name) {
            inputFieldsUpdated = true
            return { ...inputField, value: collectedTrFields.name }
          }

          return inputField
        })

        if (inputFieldsUpdated) {
          setLocalInputFields(newLocalInputFields)
          props.onInputFieldsChange?.(newLocalInputFields)
        }

        if (!hasMissingRequiredInputValues(newLocalInputFields)) {
          setInputFieldsConfirmed(true)
        }
      }}
      prefilledData={travelRulePrefilledData}
    />
    : null

  const isHexColor = (color: string | undefined): boolean | undefined => {
    if (!color) return
    return /^#?[0-9A-Fa-f]{6}$/.test(color)
  }

  const getContrastColor = (hexColor: string | undefined): string | undefined => {
    if (!hexColor) return

    const isHexColorCheck = isHexColor(hexColor)

    if (!isHexColorCheck) return

    const hex = hexColor.replace('#', '')

    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)

    const yiq = (r * 299 + g * 587 + b * 114) / 1000

    return yiq >= 128 ? '#000000' : '#FFFFFF'
  }

  const displayManagedMethods = useMemo(() => {
    const methods = (props.checkoutData.gateway.managed?.methods ?? []).filter(m => !isDelistedMethod(m.network, m.token))
    return methods.map((rawMethod) => {
      const sourceMethod = getMethod(rawMethod.network, rawMethod.token)
      const method: ManagedPaymentMethod = {
        ...sourceMethod,
        tokenInfo: sourceMethod.tokenInfo ? { ...sourceMethod.tokenInfo } : undefined
      }

      // Keep ETH visual treatment consistent with Stripe-like flow expectations.
      if (method.ticker === 'ETH') {
        if (method.tokenInfo) {
          method.tokenInfo.logo = EthLogo
        } else {
          method.logo = EthLogo
        }
      }

      return {
        rawMethod,
        method
      }
    })
  }, [props.checkoutData.gateway.managed?.methods])

  const hasMissingRequiredInputFields = useMemo(() => {
    return hasMissingRequiredInputValues(localInputFields)
  }, [localInputFields])

  const getManagedMethodDiscount = (discountPercentOff: number | undefined, ticker: string): number => {
    if (discountPercentOff && discountPercentOff > 0) {
      return discountPercentOff
    }

    if (!props.discount) {
      return 0
    }

    if (props.discount.model.modelType === 'percentage') {
      return Number(props.discount.model.amountOff) / 100
    }

    const fixedAmount = Number(props.discount.model.amountOff)
    const convertedFixed = convertValue(
      fixedAmount,
      props.currency,
      ticker,
      priceQuotes,
      false
    )
    const baseValue = convertValue(
      totalSum,
      props.currency,
      ticker,
      priceQuotes,
      false
    )

    if (!Number.isFinite(baseValue) || baseValue <= 0) {
      return 0
    }

    return Math.max(0, Math.min(convertedFixed / baseValue, 1))
  }

  const currencyOptions = useMemo(() => {
    const byTicker = new Map<string, {
      ticker: string
      displayName: string
      logo?: string
      networkSet: Set<string>
      discount: number
    }>()

    for (const entry of displayManagedMethods) {
      const networkName = entry.method.tokenInfo?.networkName ?? entry.method.name
      const existing = byTicker.get(entry.method.ticker)
      const discount = getManagedMethodDiscount(entry.rawMethod.discountPercentOff, entry.method.ticker)
      if (!existing) {
        byTicker.set(entry.method.ticker, {
          ticker: entry.method.ticker,
          displayName: entry.method.currencyName ?? entry.method.name,
          logo: entry.method.logo,
          networkSet: new Set([networkName]),
          discount
        })
        continue
      }

      existing.networkSet.add(networkName)
      existing.discount = Math.max(existing.discount, discount)
    }

    return Array.from(byTicker.values()).map((entry) => ({
      ...entry,
      networkCount: entry.networkSet.size,
      amount: convertValue(
        totalSum,
        props.currency,
        entry.ticker,
        priceQuotes,
        props.checkoutData.chargeCustomerNetworkFee,
        entry.discount
      )
    }))
  }, [
    displayManagedMethods,
    priceQuotes,
    props.checkoutData.chargeCustomerNetworkFee,
    props.currency,
    totalSum
  ])

  const normalizedCurrencySearch = currencySearch.trim().toLowerCase()
  const showCurrencySearch = currencyOptions.length >= CURRENCY_SEARCH_THRESHOLD

  const filteredCurrencyOptions = useMemo(() => {
    if (!showCurrencySearch || normalizedCurrencySearch.length === 0) {
      return currencyOptions
    }

    return currencyOptions.filter(option => {
      const haystack = [
        option.ticker,
        option.displayName
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      return haystack.includes(normalizedCurrencySearch)
    })
  }, [currencyOptions, normalizedCurrencySearch, showCurrencySearch])

  const selectedTickerNetworkOptions = useMemo(() => {
    if (!selectedTicker) {
      return []
    }

    const rows = displayManagedMethods
      .filter(({ method }) => method.ticker === selectedTicker)
      .map(({ rawMethod, method }) => {
        const networkName = method.tokenInfo?.networkName ?? method.name
        const discount = getManagedMethodDiscount(rawMethod.discountPercentOff, method.ticker)
        return {
          rawMethod,
          method,
          networkName,
          optionKey: `${method.ticker.toUpperCase()}:${networkName.trim().toLowerCase()}`,
          isTestnet: !!method.isTestnet,
          amount: convertValue(
            totalSum,
            props.currency,
            method.ticker,
            priceQuotes,
            props.checkoutData.chargeCustomerNetworkFee,
            discount
          )
        }
      })

    const uniqueByNetwork = new Map<string, typeof rows[0]>()
    for (const row of rows) {
      if (!uniqueByNetwork.has(row.optionKey)) {
        uniqueByNetwork.set(row.optionKey, row)
      }
    }

    return Array.from(uniqueByNetwork.values())
  }, [
    displayManagedMethods,
    priceQuotes,
    props.checkoutData.chargeCustomerNetworkFee,
    props.currency,
    selectedTicker,
    totalSum
  ])

  const proceedWithManagedMethod = async (method: ManagedPaymentMethod): Promise<void> => {
    if (isLoading) {
      return
    }

    setManagedRouteError('')
    setIsStepTransitioning(true)
    setSelectedMethod(method)
    setSelectedChainName(method.tokenInfo?.networkName ?? method.name)
    setSelectedTicker(method.ticker)
    setSelectedTokenImageUrl(method.logo ?? '')

    const transitionStartedAt = Date.now()
    setIsLoading(true)
    try {
      if (recurringInterval() > 0) {
        setManagedSubscriptionState(SubscriptionCheckoutState.Select)
        return
      }

      await props.onCheckout({
        managed: { selectedMethod: method },
        priceQuotes,
        travelRuleFields
      })
    } catch (err) {
      console.error('Failed to initialize managed payment method', err)
      autoAdvanceRef.current = ''
      setSelectedMethod(undefined)
      setSelectedChainName('')
      setManagedRouteError(err instanceof Error ? err.message : 'Unable to prepare your payment route.')
    } finally {
      const remainingDelay = Math.max(0, MANAGED_STEP_TRANSITION_DELAY_MS - (Date.now() - transitionStartedAt))
      if (remainingDelay > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingDelay))
      }
      setIsStepTransitioning(false)
      setIsLoading(false)
    }
  }

  const autoAdvanceRef = useRef<string>('')

  const resetManagedPaymentSelection = (): void => {
    autoAdvanceRef.current = ''
    setSelectedMethod(undefined)
    setSelectedTicker('')
    setSelectedChainName('')
    setSelectedTokenImageUrl('')
    setManagedRouteError('')
    setManagedSubscriptionState(undefined)
    setIsStepTransitioning(false)
    setIsLoading(false)
    props.onChangePaymentMethod?.()
  }

  useEffect(() => {
    if (checkoutState !== CheckoutState.Checkout) {
      return
    }

    if (props.managedPaymentDetails) {
      return
    }

    if (managedSubscriptionState !== undefined) {
      return
    }

    if (props.posMode ?? false) {
      return
    }

    if (
      displayManagedMethods.length === 0 ||
      !inputFieldsConfirmed ||
      hasMissingRequiredInputFields ||
      isLoadingPriceQuotes
    ) {
      return
    }

    if (currencyOptions.length === 1 && !selectedTicker) {
      const autoTicker = currencyOptions[0].ticker
      setSelectedTicker(autoTicker)
      setSelectedTokenImageUrl(currencyOptions[0].logo ?? '')
      return
    }

    if (selectedTicker && selectedTickerNetworkOptions.length === 1 && !selectedMethod) {
      const autoMethod = selectedTickerNetworkOptions[0].method
      const autoKey = `${props.checkoutData.id}:${selectedTickerNetworkOptions[0].optionKey}`
      if (autoAdvanceRef.current === autoKey) {
        return
      }
      autoAdvanceRef.current = autoKey
      void proceedWithManagedMethod(autoMethod)
    }
  }, [
    checkoutState,
    currencyOptions,
    displayManagedMethods.length,
    hasMissingRequiredInputFields,
    inputFieldsConfirmed,
    isLoadingPriceQuotes,
    managedSubscriptionState,
    props.checkoutData.id,
    props.managedPaymentDetails,
    props.posMode,
    selectedMethod,
    selectedTicker,
    selectedTickerNetworkOptions
  ])

  const managedDisplayMethod = useMemo(() => {
    if (!props.managedPaymentDetails) {
      return undefined
    }

    const source = getMethod(props.managedPaymentDetails.network, props.managedPaymentDetails.token)
    const method = {
      ...source,
      tokenInfo: source.tokenInfo ? { ...source.tokenInfo } : undefined
    }

    if (method.ticker === 'ETH') {
      if (method.tokenInfo) {
        method.tokenInfo.logo = EthLogo
      } else {
        method.logo = EthLogo
      }
    }

    return method
  }, [props.managedPaymentDetails])

  const paymentNetworkName = managedDisplayMethod?.tokenInfo?.networkName ?? managedDisplayMethod?.name
  const paymentTicker = managedDisplayMethod?.ticker ?? selectedMethod?.ticker ?? ''
  const paymentAddress = props.managedPaymentDetails?.paymentAddress ?? ''
  const paymentAmount = props.managedPaymentDetails?.amount ?? ''
  const paymentReference = props.managedPaymentDetails?.paymentId ?? ''
  const sellerDisplayName = props.checkoutData.sellerName?.trim() ?? ''
  const sellerAddress = props.checkoutData.sellerAddress?.trim() ?? ''
  const sellerNameLooksLikeFallback =
    sellerDisplayName.length === 0 ||
    sellerDisplayName === sellerAddress ||
    sellerDisplayName.includes('@') ||
    /^0x[a-fA-F0-9]{40}$/.test(sellerDisplayName)
  const receiptRecipientName = sellerNameLooksLikeFallback ? '' : sellerDisplayName

  const secondsRemaining = Math.max(
    0,
    Math.floor(
      (
        new Date(props.paymentStatus?.expiresAt ?? props.managedPaymentDetails?.expiresAt ?? new Date()).getTime() -
        new Date().getTime()
      ) / 1000
    )
  )

  const lastTxHash = props.paymentStatus?.transactions[0]?.transactionHash
  const networkExplorer = props.managedPaymentDetails
    ? (NetworkToChainMap[props.managedPaymentDetails.network] as any)?.blockExplorers?.default?.url
    : undefined
  const txExplorerUrl = networkExplorer && lastTxHash ? `${networkExplorer}/tx/${lastTxHash}` : undefined

  const showWalletConnectArea = !props.posMode &&
    paymentStatus !== 'expired' &&
    props.checkoutData.organizationId !== 'f58cf6b3-b87d-4893-b859-cddc85ac0abf' &&
    !!managedDisplayMethod?.isWalletSupported &&
    !isBitcoinNetwork

  const shouldUseManagedStepShellFlow =
    !!props.checkoutData.gateway.managed &&
    !props.posMode

  const selectedMethodInitialAmount = useMemo(() => {
    if (!selectedMethod) {
      return '—'
    }

    return formatCrypto8(
      convertValue(
        totalSum,
        props.currency,
        selectedMethod.ticker,
        priceQuotes,
        props.checkoutData.chargeCustomerNetworkFee
      )
    )
  }, [
    priceQuotes,
    props.checkoutData.chargeCustomerNetworkFee,
    props.currency,
    selectedMethod,
    totalSum
  ])

  const selectedMethodRecurringAmount = useMemo(() => {
    if (!selectedMethod) {
      return '—'
    }

    return formatCrypto8(
      convertValue(
        recurringSum,
        props.currency,
        selectedMethod.ticker,
        priceQuotes,
        false
      )
    )
  }, [priceQuotes, props.currency, recurringSum, selectedMethod])

  const shellInputStyle: CSSProperties = {
    minHeight: 44,
    borderRadius: 10,
    fontSize: 15,
    lineHeight: 1.3,
    padding: '10px 12px',
    borderColor: '#d8dee9',
    backgroundColor: '#ffffff'
  }

  const inputValidationStyle = (hasError: boolean, baseStyle: CSSProperties = shellInputStyle): CSSProperties => ({
    ...baseStyle,
    ...(hasError ? INPUT_ERROR_STYLE : undefined)
  })

  const shellLabelStyle: CSSProperties = {
    fontSize: 14,
    fontWeight: 500,
    color: '#495774'
  }

  const primaryButtonStyle: CSSProperties = {
    width: '100%',
    minHeight: 46,
    borderRadius: 11,
    fontWeight: 500,
    background: props.checkoutData.customizations?.primaryButtonColor,
    color: getContrastColor(props.checkoutData.customizations?.primaryButtonColor) ?? '#ffffff'
  }

  const secondaryButtonStyle: CSSProperties = {
    borderColor: '#d9e0eb',
    color: '#4c5c78',
    background: '#ffffff',
    borderRadius: 10,
    minHeight: 40,
    fontSize: 14,
    fontWeight: 500
  }

  const receiptEmailFromInput = useMemo(() => {
    const inputEmail = localInputFields.find((field) => field.dataType === 'Email')
    const fallback = typeof inputEmail?.value === 'string' ? inputEmail.value.trim() : ''
    const travelRuleEmail = travelRuleFields.emailAddress?.trim()
    const checkoutEmail = props.checkoutData.travelRuleEmail?.trim()
    return travelRuleEmail ?? checkoutEmail ?? fallback ?? ''
  }, [localInputFields, props.checkoutData.travelRuleEmail, travelRuleFields.emailAddress])

  const formatSelectableAmount = (value: number): string => {
    if (!Number.isFinite(value)) {
      return '—'
    }

    return formatCrypto8(value)
  }

  const focusFirstInvalidInputField = (index: number): void => {
    window.requestAnimationFrame(() => {
      const fieldElement =
        inputRefs.current[index] ??
        document.querySelector<HTMLElement>(`[data-input-field-index="${index}"]`)

      fieldElement?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      if (fieldElement?.matches('input')) {
        fieldElement.focus()
      } else {
        fieldElement?.querySelector<HTMLElement>('input, button, [tabindex]:not([tabindex="-1"])')?.focus()
      }
    })
  }

  const validateRequiredInputFields = (): boolean => {
    const firstInvalidIndex = localInputFields.findIndex(field => inputFieldValidationError(field) !== undefined)

    if (firstInvalidIndex >= 0) {
      setInputFieldValidationAttempted(true)
      focusFirstInvalidInputField(firstInvalidIndex)
      return false
    }

    setInputFieldValidationAttempted(false)
    return true
  }

  const handleInputFieldsContinue = (): void => {
    if (!validateRequiredInputFields()) {
      return
    }

    setInputFieldsConfirmed(true)
  }

  const validateSubscriptionStep = (): boolean => {
    if (selectedSubscriptionType === undefined) {
      setSubscriptionValidationError('Choose how to pay for future renewals.')
      return false
    }

    const emailRequired =
      selectedSubscriptionType === SubscriptionType.EmailInvoice ||
      (selectedSubscriptionType === SubscriptionType.Autopay && receiveEmailNotifications)
    const emailValue = subscriptionEmailAddress.trim()

    if (emailRequired && emailValue.length === 0) {
      setSubscriptionValidationError('Enter email address.')
      return false
    }

    if (emailValue.length > 0 && !simpleEmailValidation(emailValue)) {
      setSubscriptionValidationError('Enter a valid email address.')
      return false
    }

    setSubscriptionValidationError('')
    return true
  }

  const renderCustomInputFields = ({
    showIntro = true
  }: {
    showIntro?: boolean
  } = {}): ReactElement | null => {
    if (!props.checkoutData.inputFields || props.checkoutData.inputFields.length === 0) {
      return null
    }

    return (
      <FormSection>
        {showIntro && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: CHECKOUT_SPACING.xs }}>
            <SectionTitle>Payer details</SectionTitle>
            <SectionHelper>Enter required details before choosing a payment method.</SectionHelper>
          </div>
        )}
        <FormFieldStack>
          {localInputFields.map((field, index) => {
            const validationError = inputFieldValidationAttempted
              ? inputFieldValidationError(field)
              : undefined
            const hasValidationError = Boolean(validationError)

            return (
              <div
                key={`${field.inputLabel}-${index}`}
                data-input-field-index={index}
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <InputLabel style={shellLabelStyle}>
                    {field.inputLabel === '' ? 'Input label' : field.inputLabel}
                  </InputLabel>
                  {!field.isRequired && <Cookie>Optional</Cookie>}
                </div>

                {field.dataType === 'Boolean' && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    minHeight: 48,
                    border: hasValidationError ? '1px solid #df1b41' : '1px solid transparent',
                    boxShadow: hasValidationError ? INPUT_ERROR_STYLE.boxShadow : undefined,
                    borderRadius: 10,
                    padding: '0 10px'
                  }}>
                    <Checkbox
                      checked={field.value === true}
                      onClick={() => {
                        const newInputs = localInputFields.slice()
                        const currentValue = newInputs[index].value === true
                        newInputs[index].value = !currentValue
                        setLocalInputFields(newInputs)
                        props.onInputFieldsChange?.(newInputs)
                      }}
                    />
                    <span style={{ color: '#1f2937' }}>Enabled</span>
                  </div>
                )}
                {field.dataType !== 'Boolean' && (
                  <TextInput
                    disabled={props.isPreProcessingPayment}
                    type={field.dataType === 'Number' ? 'number' : 'text'}
                    placeholder={
                      field.dataType === 'Number'
                        ? 'Enter a number'
                        : `Enter ${field.inputLabel.toLowerCase()}`
                    }
                    value={field.value as string | number}
                    required={field.isRequired}
                    aria-invalid={hasValidationError}
                    style={inputValidationStyle(hasValidationError)}
                    ref={el => inputRefs.current[index] = el}
                    onInput={(event) => {
                      const newInputs = localInputFields.slice()
                      const value = (event.target as HTMLInputElement).value
                      if (field.dataType === 'Email') {
                        newInputs[index].value = value.toLowerCase()
                      } else {
                        newInputs[index].value = value
                      }
                      setLocalInputFields(newInputs)
                      props.onInputFieldsChange?.(newInputs)
                    }}
                  />
                )}
                {validationError && <InlineInputError>{validationError}</InlineInputError>}
              </div>
            )
          })}
        </FormFieldStack>
      </FormSection>
    )
  }

  const handleSelectCurrency = (ticker: string, logo?: string): void => {
    if (isLoading) {
      return
    }

    setManagedRouteError('')
    setIsStepTransitioning(true)
    setCurrencySearch('')
    setSelectedTicker(ticker)
    setSelectedTokenImageUrl(logo ?? '')
    setSelectedChainName('')
    setSelectedMethod(undefined)

    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current)
    }

    transitionTimeoutRef.current = setTimeout(() => {
      setIsStepTransitioning(false)
      transitionTimeoutRef.current = null
    }, MANAGED_STEP_TRANSITION_DELAY_MS)
  }

  const redirectToSuccessUrl = (): void => {
    if (!props.checkoutData.successUrl) {
      return
    }

    const resolved = props.checkoutData.successUrl.replace(
      '{CHECKOUT_SESSION_ID}',
      props.checkoutData.id
    )
    window.location.href = resolveNavigationUrl(resolved)
  }

  if ((!props.isSuccess || props.managedPaymentDetails) && shouldUseManagedStepShellFlow) {
    if (checkoutState === CheckoutState.CollectTravelRuleFields && trfWidget) {
      return (
        <Wrapper disableAnimation={props.disableAnimation}>
          <Container disableAnimation={props.disableAnimation}>
            <StepShell key="billing-information" compact>
              <StepHeader compact>
                <StepEyebrow>Secure checkout</StepEyebrow>
                <StepTitle compact>Billing information</StepTitle>
              </StepHeader>
              {trfWidget}
            </StepShell>
          </Container>
        </Wrapper>
      )
    }

    if (
      !props.managedPaymentDetails &&
      managedSubscriptionState === undefined &&
      localInputFields.length > 0 &&
      !inputFieldsConfirmed
    ) {
      return (
        <Wrapper disableAnimation={props.disableAnimation}>
          <Container disableAnimation={props.disableAnimation}>
            <StepShell key="payer-details" compact>
              <StepHeader compact>
                <StepEyebrow>Secure checkout</StepEyebrow>
                <StepTitle compact>Payer details</StepTitle>
                <StepSubtitle>Confirm your details before choosing a payment method.</StepSubtitle>
              </StepHeader>
              {renderCustomInputFields({ showIntro: false })}
              <PrimaryButton
                style={primaryButtonStyle}
                disabled={props.isPreProcessingPayment}
                onClick={handleInputFieldsContinue}
              >
                Continue
              </PrimaryButton>
            </StepShell>
          </Container>
        </Wrapper>
      )
    }

    if (!props.managedPaymentDetails && managedSubscriptionState === undefined) {
      const showCurrencySelection = !selectedTicker && currencyOptions.length > 1 && !isStepTransitioning
      const showNetworkSelection =
        !!selectedTicker &&
        selectedTickerNetworkOptions.length > 1 &&
        !selectedMethod &&
        !isStepTransitioning
      const showPreparingState = isStepTransitioning || (!showCurrencySelection && !showNetworkSelection)
      const stepTransitionKey = showCurrencySelection
        ? 'currency'
        : showNetworkSelection
          ? 'network'
          : 'preparing'
      const selectedCurrencyMeta = currencyOptions.find(option => option.ticker === selectedTicker)

      return (
        <Wrapper disableAnimation={props.disableAnimation}>
          <Container disableAnimation={props.disableAnimation}>
            <StepShell key="payment-method-selection">
              <StepHeader>
                <StepEyebrow>Secure checkout</StepEyebrow>
                <StepTitle>
                  {showNetworkSelection ? 'Choose network' : 'Choose how to pay'}
                </StepTitle>
                <StepSubtitle>
                  {showNetworkSelection
                    ? `Select the network you'll use to send ${selectedTicker}.`
                    : 'Select the cryptocurrency you want to use for this payment.'}
                </StepSubtitle>
              </StepHeader>

              <StepTransitionFrame key={stepTransitionKey}>
                {showCurrencySelection && (
                  <FormSection>
                    <SelectorTools>
                      {showCurrencySearch && (
                        <SelectorSearchInput
                          placeholder='Search currencies'
                          value={currencySearch}
                          onInput={(event) => setCurrencySearch((event.target as HTMLInputElement).value)}
                        />
                      )}

                      <SelectableList>
                        {filteredCurrencyOptions.length > 0
                          ? filteredCurrencyOptions.map(option => (
                            <SelectableRow
                              key={option.ticker}
                              selected={selectedTicker === option.ticker}
                              disabled={isLoading || props.isPreProcessingPayment}
                              onClick={() => handleSelectCurrency(option.ticker, option.logo)}
                            >
                              <SelectableRowLeft>
                                <SelectableRowIcon src={option.logo} />
                                <SelectableRowText>
                                  <SelectableRowTitle>{option.displayName}</SelectableRowTitle>
                                  <SelectableRowMeta>
                                    {option.ticker}
                                    {option.networkCount > 1
                                      ? ` · ${option.networkCount} networks`
                                      : ' · 1 network'}
                                  </SelectableRowMeta>
                                </SelectableRowText>
                              </SelectableRowLeft>
                              <SelectableRowValue>
                                {formatSelectableAmount(option.amount)} {option.ticker}
                              </SelectableRowValue>
                            </SelectableRow>
                          ))
                          : (
                            <div style={{
                              padding: '18px 20px',
                              fontSize: 14,
                              color: '#64748b'
                            }}>
                              No currencies match your search.
                            </div>
                          )}
                      </SelectableList>
                    </SelectorTools>
                  </FormSection>
                )}

                {showNetworkSelection && (
                  <FormSection>
                    <SelectableList>
                      {selectedTickerNetworkOptions.map(option => (
                        <SelectableRow
                          key={option.optionKey}
                          selected={selectedChainName === option.networkName}
                          disabled={isLoading || props.isPreProcessingPayment}
                          onClick={() => {
                            void proceedWithManagedMethod(option.method)
                          }}
                        >
                          <SelectableRowLeft>
                            <PaymentMethodIcon method={option.method} />
                            <SelectableRowText>
                              <SelectableRowTitle>{option.networkName}</SelectableRowTitle>
                              <SelectableRowMeta>{option.method.ticker}</SelectableRowMeta>
                            </SelectableRowText>
                          </SelectableRowLeft>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {option.isTestnet && <Badge>Testnet</Badge>}
                            <SelectableRowValue>
                              {formatSelectableAmount(option.amount)} {option.method.ticker}
                            </SelectableRowValue>
                          </div>
                        </SelectableRow>
                      ))}
                    </SelectableList>
                    <SecondaryButton
                      style={secondaryButtonStyle}
                      onClick={() => {
                        setSelectedTicker('')
                        setSelectedChainName('')
                        setSelectedMethod(undefined)
                      }}
                      disabled={isLoading || props.isPreProcessingPayment}
                    >
                      Back to currencies
                    </SecondaryButton>
                  </FormSection>
                )}

                {showPreparingState && (
                  <FormSection>
                    <PaymentDetailsBlock>
                      {displayManagedMethods.length === 0
                        ? (
                          <PaymentDetailRow>
                            <DetailLabel>Payment method</DetailLabel>
                            <DetailValue>No crypto methods available</DetailValue>
                          </PaymentDetailRow>
                        )
                        : (
                          <PaymentDetailRow>
                            <DetailLabel>Selected currency</DetailLabel>
                            <DetailValue>{selectedCurrencyMeta?.displayName ?? 'Preparing payment options'}</DetailValue>
                          </PaymentDetailRow>
                        )}
                      {selectedCurrencyMeta && (
                        <PaymentDetailRow>
                          <DetailLabel>Amount due</DetailLabel>
                          <DetailValue>
                            {formatSelectableAmount(selectedCurrencyMeta.amount)} {selectedCurrencyMeta.ticker}
                          </DetailValue>
                        </PaymentDetailRow>
                      )}
                    </PaymentDetailsBlock>
                    <LoadingStateGroup>
                      {managedRouteError.length > 0 || props.errorMessage
                        ? (
                          <SectionHelper style={{ color: '#b45309', textAlign: 'center' }}>
                            {managedRouteError || props.errorMessage}
                          </SectionHelper>
                        )
                        : displayManagedMethods.length === 0
                          ? <SectionHelper style={{ textAlign: 'center' }}>Please refresh or try another checkout method.</SectionHelper>
                          : isStepTransitioning || isLoading || isLoadingPriceQuotes
                            ? (
                              <>
                                <LoaderWrapper />
                                <SectionHelper style={{ textAlign: 'center' }}>Preparing your payment route...</SectionHelper>
                              </>
                            )
                            : <SectionHelper style={{ textAlign: 'center' }}>Preparing your payment route...</SectionHelper>}
                    </LoadingStateGroup>
                    {(Boolean(selectedTicker) || selectedMethod !== undefined) && (
                      <SecondaryButton
                        style={secondaryButtonStyle}
                        onClick={resetManagedPaymentSelection}
                      >
                        Change payment method
                      </SecondaryButton>
                    )}
                  </FormSection>
                )}
              </StepTransitionFrame>
            </StepShell>
          </Container>
        </Wrapper>
      )
    }

    if (
      !props.managedPaymentDetails &&
      managedSubscriptionState === SubscriptionCheckoutState.Select &&
      selectedMethod
    ) {
      const selectedNetworkName = selectedMethod.tokenInfo?.networkName ?? selectedMethod.name
      const subscriptionIntervalLabel = formatSubscriptionDuration(recurringInterval() * 60 * 60 * 24).toLowerCase()
      const canUseAutopay = isAutopayEnabled(selectedMethod)
      const showNotificationEmailField =
        selectedSubscriptionType === SubscriptionType.Autopay &&
        (receiveEmailNotifications || subscriptionEmailAddress.length === 0)

      return (
        <Wrapper disableAnimation={props.disableAnimation}>
          <Container disableAnimation={props.disableAnimation}>
            <StepShell key="subscription-setup">
              <StepHeader>
                <StepEyebrow>Secure checkout</StepEyebrow>
                <StepTitle>Set up your subscription</StepTitle>
                <StepSubtitle>Choose how you&apos;d like to pay future renewals.</StepSubtitle>
              </StepHeader>

              <PaymentDetailsBlock>
                <PaymentDetailRow>
                  <DetailLabel>Payment method</DetailLabel>
                  <DetailValue>{selectedMethod.ticker}</DetailValue>
                </PaymentDetailRow>
                <PaymentDetailRow>
                  <DetailLabel>Network</DetailLabel>
                  <DetailValue>{selectedNetworkName}</DetailValue>
                </PaymentDetailRow>
                <PaymentDetailRow>
                  <DetailLabel>First payment</DetailLabel>
                  <DetailValue>{selectedMethodInitialAmount} {selectedMethod.ticker}</DetailValue>
                </PaymentDetailRow>
                <PaymentDetailRow>
                  <DetailLabel>Recurring payment</DetailLabel>
                  <DetailValue>
                    {selectedMethodRecurringAmount} {selectedMethod.ticker} / {subscriptionIntervalLabel}
                  </DetailValue>
                </PaymentDetailRow>
              </PaymentDetailsBlock>

              <FormSection>
                <SelectableList>
                  <SelectableRow
                    selected={selectedSubscriptionType === SubscriptionType.Autopay}
                    disabled={!canUseAutopay || props.isPreProcessingPayment || isLoadingAllowance}
                    onClick={() => {
                      if (!canUseAutopay) {
                        return
                      }

                      setSubscriptionValidationError('')
                      setSelectedSubscriptionType(SubscriptionType.Autopay)
                      const { emailAddress } = travelRuleFields
                      const { travelRuleEmail } = props.checkoutData
                      const trfEmail = emailAddress ?? travelRuleEmail ?? ''
                      setSubscriptionEmailAddress(trfEmail)
                    }}
                    style={!canUseAutopay ? { opacity: 0.5, cursor: 'not-allowed' } as any : undefined}
                  >
                    <SelectableRowLeft>
                      <SelectableRowText>
                        <SelectableRowTitle>Auto-renew with wallet</SelectableRowTitle>
                        <SelectableRowMeta>
                          Set an allowance once and let future renewals be charged automatically.
                        </SelectableRowMeta>
                      </SelectableRowText>
                    </SelectableRowLeft>
                    {!canUseAutopay && <Badge>Unavailable</Badge>}
                  </SelectableRow>
                  <SelectableRow
                    selected={selectedSubscriptionType === SubscriptionType.EmailInvoice}
                    disabled={props.isPreProcessingPayment || isLoadingAllowance}
                    onClick={() => {
                      setSubscriptionValidationError('')
                      setSelectedSubscriptionType(SubscriptionType.EmailInvoice)
                      const { emailAddress } = travelRuleFields
                      const { travelRuleEmail } = props.checkoutData
                      const trfEmail = emailAddress ?? travelRuleEmail ?? ''
                      setSubscriptionEmailAddress(trfEmail)
                    }}
                  >
                    <SelectableRowLeft>
                      <SelectableRowText>
                        <SelectableRowTitle>Email invoices</SelectableRowTitle>
                        <SelectableRowMeta>
                          Receive renewal invoices by email and pay each billing cycle manually.
                        </SelectableRowMeta>
                      </SelectableRowText>
                    </SelectableRowLeft>
                  </SelectableRow>
                </SelectableList>
                {selectedSubscriptionType === undefined && subscriptionValidationError && (
                  <InlineInputError>{subscriptionValidationError}</InlineInputError>
                )}

                {selectedSubscriptionType === SubscriptionType.EmailInvoice && (
                  <FormFieldStack>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: CHECKOUT_SPACING.xs }}>
                      <InputLabel style={shellLabelStyle}>Email address</InputLabel>
                      <TextInput
                        autoFocus
                        placeholder='Enter email address'
                        value={subscriptionEmailAddress}
                        aria-invalid={Boolean(subscriptionValidationError)}
                        style={inputValidationStyle(Boolean(subscriptionValidationError))}
                        onChange={e => {
                          setSubscriptionValidationError('')
                          setSubscriptionEmailAddress(e.target.value.toLowerCase())
                        }}
                      />
                      {subscriptionValidationError && (
                        <InlineInputError>{subscriptionValidationError}</InlineInputError>
                      )}
                    </div>
                  </FormFieldStack>
                )}

                {selectedSubscriptionType === SubscriptionType.Autopay && (
                  <FormFieldStack>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        color: '#475569',
                        cursor: 'pointer',
                        fontSize: 14
                      }}
                    >
                      <Checkbox checked={receiveEmailNotifications} />
                      <span>Send me email notifications about this subscription</span>
                    </label>

                    {showNotificationEmailField && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: CHECKOUT_SPACING.xs }}>
                        <InputLabel style={shellLabelStyle}>Email address</InputLabel>
                        <TextInput
                          autoFocus
                          placeholder='Enter email address'
                          value={subscriptionEmailAddress}
                          aria-invalid={Boolean(subscriptionValidationError)}
                          style={inputValidationStyle(Boolean(subscriptionValidationError))}
                          onChange={e => {
                            setSubscriptionValidationError('')
                            setSubscriptionEmailAddress(e.target.value.toLowerCase())
                          }}
                        />
                        {subscriptionValidationError && (
                          <InlineInputError>{subscriptionValidationError}</InlineInputError>
                        )}
                      </div>
                    )}

                    {address && (
                      <PaymentDetailsBlock>
                        <PaymentDetailRow>
                          <DetailLabel>Connected wallet</DetailLabel>
                          <DetailValue>{shortAddress(address, 8)}</DetailValue>
                        </PaymentDetailRow>
                      </PaymentDetailsBlock>
                    )}
                  </FormFieldStack>
                )}
              </FormSection>

              <CTASection>
                <PrimaryButton
                  isLoading={
                    isLoadingPriceQuotes ||
                    isSettingAllowance ||
                    props.isPreProcessingPayment ||
                    isLoadingAllowance
                  }
                  disabled={
                    props.isPreProcessingPayment ||
                    isLoadingPriceQuotes ||
                    isSettingAllowance ||
                    isLoadingAllowance
                  }
                  style={primaryButtonStyle}
                  onClick={async () => {
                    if (!validateSubscriptionStep()) {
                      return
                    }

                    if (selectedSubscriptionType === SubscriptionType.Autopay && !isConnected) {
                      await open()
                      return
                    }

                    if (!selectedMethod || selectedSubscriptionType === undefined) {
                      return
                    }

                    props.onCheckout({
                      managed: {
                        selectedMethod
                      },
                      priceQuotes,
                      subscriptionOptions: {
                        autopay: selectedSubscriptionType === SubscriptionType.Autopay
                          ? {
                            subscriptionType: selectedSubscriptionType,
                            address: address as any
                          }
                          : undefined,
                        emailInvoice: selectedSubscriptionType === SubscriptionType.EmailInvoice
                          ? {
                            subscriptionType: selectedSubscriptionType,
                            emailAddress: subscriptionEmailAddress.toLowerCase()
                          }
                          : undefined
                      },
                      travelRuleFields
                    })
                  }}
                >
                  {selectedSubscriptionType === SubscriptionType.Autopay && !isConnected
                    ? 'Connect wallet to continue'
                    : 'Continue'}
                </PrimaryButton>

                <SecondaryButton
                  disabled={
                    isLoadingPriceQuotes ||
                    isSettingAllowance ||
                    isLoadingAllowance ||
                    props.isPreProcessingPayment
                  }
                  style={secondaryButtonStyle}
                  onClick={resetManagedPaymentSelection}
                >
                  Change payment method
                </SecondaryButton>
              </CTASection>
            </StepShell>
          </Container>
        </Wrapper>
      )
    }

    if (
      !props.managedPaymentDetails &&
      managedSubscriptionState === SubscriptionCheckoutState.Allowance &&
      selectedMethod
    ) {
      const selectedNetworkName = selectedMethod.tokenInfo?.networkName ?? selectedMethod.name
      const subscriptionIntervalLabel = formatSubscriptionDuration(
        recurringInterval() * 60 * 60 * 24
      ).toLowerCase()

      return (
        <Wrapper disableAnimation={props.disableAnimation}>
          <Container disableAnimation={props.disableAnimation}>
            <StepShell key="subscription-allowance" compact>
              <StepHeader compact>
                <StepEyebrow>Secure checkout</StepEyebrow>
                <StepTitle compact>Approve recurring payments</StepTitle>
                <StepSubtitle compact>
                  Set an allowance once so future renewals can be charged automatically.
                </StepSubtitle>
              </StepHeader>

              <PaymentDetailsBlock>
                <PaymentDetailRow>
                  <DetailLabel>Initial payment</DetailLabel>
                  <DetailValue>{selectedMethodInitialAmount} {selectedMethod.ticker}</DetailValue>
                </PaymentDetailRow>
                <PaymentDetailRow>
                  <DetailLabel>Recurring payment</DetailLabel>
                  <DetailValue>
                    {selectedMethodRecurringAmount} {selectedMethod.ticker} / {subscriptionIntervalLabel}
                  </DetailValue>
                </PaymentDetailRow>
                <PaymentDetailRow>
                  <DetailLabel>Network</DetailLabel>
                  <DetailValue>{selectedNetworkName}</DetailValue>
                </PaymentDetailRow>
              </PaymentDetailsBlock>

              <CTASection>
                <PrimaryButton
                  isLoading={isLoadingPriceQuotes || isSettingAllowance}
                  style={primaryButtonStyle}
                  onClick={() => {
                    void setManagedSubscriptionAllowance()
                  }}
                >
                  Set allowance
                </PrimaryButton>

                {allowSkipAllowance && (
                  <SecondaryButton
                    disabled={isLoadingPriceQuotes || isSettingAllowance}
                    style={secondaryButtonStyle}
                    onClick={() => setManagedSubscriptionState(SubscriptionCheckoutState.Confirm)}
                  >
                    Continue without allowance
                  </SecondaryButton>
                )}

                <SecondaryButton
                  disabled={isLoadingPriceQuotes || isSettingAllowance}
                  style={secondaryButtonStyle}
                  onClick={resetManagedPaymentSelection}
                >
                  Change payment method
                </SecondaryButton>
              </CTASection>
            </StepShell>
          </Container>
        </Wrapper>
      )
    }

    if (
      !props.managedPaymentDetails &&
      managedSubscriptionState === SubscriptionCheckoutState.Confirm &&
      selectedMethod
    ) {
      const selectedNetworkName = selectedMethod.tokenInfo?.networkName ?? selectedMethod.name
      const subscriptionIntervalLabel = formatSubscriptionDuration(
        recurringInterval() * 60 * 60 * 24
      ).toLowerCase()

      return (
        <Wrapper disableAnimation={props.disableAnimation}>
          <Container disableAnimation={props.disableAnimation}>
            <StepShell key="subscription-confirm" compact>
              <StepHeader compact>
                <StepEyebrow>Secure checkout</StepEyebrow>
                <StepTitle compact>Review subscription</StepTitle>
                <StepSubtitle compact>
                  Confirm the first payment and the recurring renewal schedule.
                </StepSubtitle>
              </StepHeader>

              <PaymentDetailsBlock>
                <PaymentDetailRow>
                  <DetailLabel>Initial payment</DetailLabel>
                  <DetailValue>{selectedMethodInitialAmount} {selectedMethod.ticker}</DetailValue>
                </PaymentDetailRow>
                <PaymentDetailRow>
                  <DetailLabel>Recurring payment</DetailLabel>
                  <DetailValue>
                    {selectedMethodRecurringAmount} {selectedMethod.ticker} / {subscriptionIntervalLabel}
                  </DetailValue>
                </PaymentDetailRow>
                <PaymentDetailRow>
                  <DetailLabel>Starts</DetailLabel>
                  <DetailValue>{nextBillingAt()}</DetailValue>
                </PaymentDetailRow>
                <PaymentDetailRow>
                  <DetailLabel>Network</DetailLabel>
                  <DetailValue>{selectedNetworkName}</DetailValue>
                </PaymentDetailRow>
                {address && (
                  <PaymentDetailRow>
                    <DetailLabel>Wallet</DetailLabel>
                    <MonoValue>{shortAddress(address, 8)}</MonoValue>
                  </PaymentDetailRow>
                )}
              </PaymentDetailsBlock>

              <CTASection>
                <PrimaryButton
                  isLoading={
                    isLoadingPriceQuotes || props.isPreProcessingPayment || isSigningMessage
                  }
                  style={primaryButtonStyle}
                  onClick={async () => {
                    if (!isConnected) {
                      await open()
                      return
                    }

                    if (!props.tokenDelegatePaymentDetails || !address) {
                      return
                    }

                    setIsSigningMessage(true)

                    try {
                      const chain = NetworkToChainMap[props.tokenDelegatePaymentDetails.network] as any
                      const provider: any = await connector?.getProvider()

                      const signer = createWalletClient({
                        chain,
                        transport: custom(provider)
                      })

                      const signature = await signer?.signMessage({
                        account: address,
                        message: props.tokenDelegatePaymentDetails.messageToSign
                      })

                      props.onCheckout({
                        travelRuleFields,
                        completeTokenDelegate: {
                          paymentId: props.tokenDelegatePaymentDetails.paymentId,
                          signature
                        },
                        priceQuotes
                      })
                    } catch (err) {
                      errorToast(err.toString())
                    }

                    setIsSigningMessage(false)
                  }}
                >
                  {isConnected ? 'Subscribe' : 'Connect wallet to subscribe'}
                </PrimaryButton>

                <SecondaryButton
                  style={secondaryButtonStyle}
                  disabled={isLoadingPriceQuotes || props.isPreProcessingPayment || isSigningMessage}
                  onClick={resetManagedPaymentSelection}
                >
                  Change payment method
                </SecondaryButton>
              </CTASection>
            </StepShell>
          </Container>
        </Wrapper>
      )
    }

    if (props.managedPaymentDetails) {
      const showReviewStep = isUnderReview
      const showPendingStep = !showReviewStep && !hasConfirmedPayment && !isUnderpaid && hasPaymentDetected
      const showSuccessStep = !showReviewStep && hasConfirmedPayment
      const isExpired = paymentStatus === 'expired'
      const paymentAmountNumber = Number(paymentAmount || 0)
      const amountDueText = formatSelectableAmount(paymentAmountNumber)
      const amountLeftText = formatSelectableAmount(Number(amountLeft || 0))
      const pendingTotalText = formatSelectableAmount(pendingAmount)
      const confirmedTotalText = formatSelectableAmount(confirmedAmount > 0 ? confirmedAmount : paymentAmountNumber)
      const amountRemainingText = formatSelectableAmount(amountRemaining)
      const displayNetworkName = paymentNetworkName ?? props.managedPaymentDetails.network
      const displayNetworkIcon = managedDisplayMethod ? getNetworkLogoForMethod(managedDisplayMethod) : undefined
      const canDisplayQr = props.checkoutData.customizations?.displayQrCode !== false
      const shouldShowQr = canDisplayQr && (!isMobilePaymentViewport || showMobileQr)
      const canChangePaymentMethod = displayManagedMethods.length > 1

      return (
        <Wrapper disableAnimation={props.disableAnimation}>
          <Container disableAnimation={props.disableAnimation}>
            <StepShell key="payment-instructions">
              {showSuccessStep && (
                <>
                  <StepHeader>
                    <StepEyebrow>Payment confirmed</StepEyebrow>
                    <StepTitle>Payment received</StepTitle>
                    <StepSubtitle>
                      Your payment has been confirmed and the merchant can now process your order.
                    </StepSubtitle>
                  </StepHeader>

                  <div style={{
                    width: 64,
                    height: 64,
                    borderRadius: 32,
                    border: '1px solid #ccead9',
                    background: '#eaf8f0',
                    color: '#1f9d58',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 28,
                    fontWeight: 600,
                    margin: '0 auto'
                  }}>✓</div>

                  <PaymentDetailsBlock>
                    <PaymentDetailRow>
                      <DetailLabel>Amount paid</DetailLabel>
                      <DetailValue>{confirmedTotalText} {paymentTicker}</DetailValue>
                    </PaymentDetailRow>
                    <PaymentDetailRow>
                      <DetailLabel>Network</DetailLabel>
                      <DetailValue style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        {displayNetworkIcon && (
                          <img
                            src={displayNetworkIcon}
                            style={{ width: 16, height: 16, borderRadius: 999 }}
                          />
                        )}
                        <span>{displayNetworkName}</span>
                      </DetailValue>
                    </PaymentDetailRow>
                    {receiptRecipientName.length > 0 && (
                      <PaymentDetailRow>
                        <DetailLabel>Recipient</DetailLabel>
                        <DetailValue>{receiptRecipientName}</DetailValue>
                      </PaymentDetailRow>
                    )}
                    {paymentAddress.length > 0 && (
                      <PaymentDetailRow>
                        <DetailLabel>Recipient address</DetailLabel>
                        <DetailValue style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <MonoValue title={paymentAddress}>{shortAddress(paymentAddress)}</MonoValue>
                          <CopyButton
                            onClick={async () => { void navigator.clipboard.writeText(paymentAddress) }}
                            style={{ padding: '3px 6px', fontSize: 10 }}
                          />
                        </DetailValue>
                      </PaymentDetailRow>
                    )}
                    {receiptEmailFromInput.length > 0 && (
                      <PaymentDetailRow>
                        <DetailLabel>Receipt</DetailLabel>
                        <DetailValue>{receiptEmailFromInput}</DetailValue>
                      </PaymentDetailRow>
                    )}
                    {paymentReference.length > 0 && (
                      <PaymentDetailRow>
                        <DetailLabel>Payment ID</DetailLabel>
                        <DetailValue style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <MonoValue title={paymentReference}>{shortAddress(paymentReference)}</MonoValue>
                          <CopyButton
                            onClick={async () => { void navigator.clipboard.writeText(paymentReference) }}
                            style={{ padding: '3px 6px', fontSize: 10 }}
                          />
                        </DetailValue>
                      </PaymentDetailRow>
                    )}
                    {lastTxHash && (
                      <PaymentDetailRow>
                        <DetailLabel>Transaction Hash</DetailLabel>
                        <DetailValue style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <MonoValue title={lastTxHash}>{shortAddress(lastTxHash)}</MonoValue>
                          <CopyButton
                            onClick={async () => { void navigator.clipboard.writeText(lastTxHash) }}
                            style={{ padding: '3px 6px', fontSize: 10 }}
                          />
                        </DetailValue>
                      </PaymentDetailRow>
                    )}
                  </PaymentDetailsBlock>

                  <CTASection>
                    {!!props.checkoutData.successUrl && (
                      <PrimaryButton style={primaryButtonStyle} onClick={redirectToSuccessUrl}>
                        Return to merchant
                      </PrimaryButton>
                    )}
                    {txExplorerUrl && (
                      <a
                        href={txExplorerUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          textAlign: 'center',
                          color: '#3f6bf5',
                          textDecoration: 'none',
                          fontSize: 14,
                          fontWeight: 500
                        }}
                      >
                        View payment details
                      </a>
                    )}
                  </CTASection>
                </>
              )}

              {showReviewStep && (
                <>
                  <StepHeader>
                    <StepEyebrow>Payment received</StepEyebrow>
                    <StepTitle>Payment under review</StepTitle>
                    <StepSubtitle>
                      We&apos;ve received your payment. It&apos;s going through a routine compliance check
                      before it is released to the merchant.
                    </StepSubtitle>
                  </StepHeader>

                  <div style={{ display: 'flex', justifyContent: 'center', margin: 20 }}>
                    <LoaderWrapper />
                  </div>

                  <PaymentDetailsBlock>
                    <PaymentDetailRow>
                      <DetailLabel>Amount paid</DetailLabel>
                      <DetailValue>{confirmedTotalText} {paymentTicker}</DetailValue>
                    </PaymentDetailRow>
                    <PaymentDetailRow>
                      <DetailLabel>Network</DetailLabel>
                      <DetailValue style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        {displayNetworkIcon && (
                          <img
                            src={displayNetworkIcon}
                            style={{ width: 16, height: 16, borderRadius: 999 }}
                          />
                        )}
                        <span>{displayNetworkName}</span>
                      </DetailValue>
                    </PaymentDetailRow>
                    {paymentReference.length > 0 && (
                      <PaymentDetailRow>
                        <DetailLabel>Payment ID</DetailLabel>
                        <DetailValue style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <MonoValue title={paymentReference}>{shortAddress(paymentReference)}</MonoValue>
                          <CopyButton
                            onClick={async () => { void navigator.clipboard.writeText(paymentReference) }}
                            style={{ padding: '3px 6px', fontSize: 10 }}
                          />
                        </DetailValue>
                      </PaymentDetailRow>
                    )}
                    {lastTxHash && (
                      <PaymentDetailRow>
                        <DetailLabel>Transaction Hash</DetailLabel>
                        <DetailValue style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <MonoValue title={lastTxHash}>{shortAddress(lastTxHash)}</MonoValue>
                          <CopyButton
                            onClick={async () => { void navigator.clipboard.writeText(lastTxHash) }}
                            style={{ padding: '3px 6px', fontSize: 10 }}
                          />
                        </DetailValue>
                      </PaymentDetailRow>
                    )}
                  </PaymentDetailsBlock>

                </>
              )}

              {showPendingStep && (
                <>
                  <StepHeader>
                    <StepEyebrow>Secure checkout</StepEyebrow>
                    <StepTitle>
                      {isAwaitingConfirmation ? 'Confirming your payment' : 'Waiting for payment'}
                    </StepTitle>
                    <StepSubtitle>
                      {isAwaitingConfirmation
                        ? 'We’ve received your transfer and are confirming it. This page updates automatically.'
                        : 'We’re watching the blockchain for your transaction.'}
                    </StepSubtitle>
                  </StepHeader>

                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <LoaderWrapper />
                  </div>

                  <StatusTimeline>
                    <StatusTimelineRow>
                      <StatusDot state='done' />
                      <span>Payment detected</span>
                    </StatusTimelineRow>
                    <StatusTimelineRow>
                      <StatusDot state={hasConfirmedPayment ? 'done' : 'active'} />
                      <span>Waiting for confirmations</span>
                    </StatusTimelineRow>
                    <StatusTimelineRow>
                      <StatusDot state={hasConfirmedPayment ? 'done' : 'pending'} />
                      <span>Confirmed</span>
                    </StatusTimelineRow>
                  </StatusTimeline>

                  <PaymentDetailsBlock>
                    <PaymentDetailRow>
                      <DetailLabel>Amount due</DetailLabel>
                      <DetailValue>{amountDueText} {paymentTicker}</DetailValue>
                    </PaymentDetailRow>
                    <PaymentDetailRow>
                      <DetailLabel>{isAwaitingConfirmation ? 'Received' : 'Detected so far'}</DetailLabel>
                      <DetailValue>
                        {isAwaitingConfirmation ? confirmedTotalText : pendingTotalText} {paymentTicker}
                      </DetailValue>
                    </PaymentDetailRow>
                    {amountRemaining > 0 && (
                      <PaymentDetailRow>
                        <DetailLabel>Remaining</DetailLabel>
                        <DetailValue>{amountRemainingText} {paymentTicker}</DetailValue>
                      </PaymentDetailRow>
                    )}
                    <PaymentDetailRow>
                      <DetailLabel>Network</DetailLabel>
                      <DetailValue style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        {displayNetworkIcon && (
                          <img
                            src={displayNetworkIcon}
                            style={{ width: 16, height: 16, borderRadius: 999 }}
                          />
                        )}
                        <span>{displayNetworkName}</span>
                      </DetailValue>
                    </PaymentDetailRow>
                  </PaymentDetailsBlock>

                  <SectionHelper style={{ textAlign: 'center' }}>
                    Keep this page open. This page updates automatically.
                  </SectionHelper>
                </>
              )}

              {!showSuccessStep && !showPendingStep && !showReviewStep && (
                <>
                  <StepHeader compact>
                    <StepHeaderTopRow>
                      <StepEyebrow>Secure checkout</StepEyebrow>
                      {!isExpired && (
                        <HeaderStatusPill>
                          <TimerPillIcon aria-hidden />
                          Time remaining
                          <span>{formatSecondsToMinutesAndSecondsString(secondsRemaining)}</span>
                        </HeaderStatusPill>
                      )}
                    </StepHeaderTopRow>
                    <StepTitle style={{ marginTop: 6 }}>
                      {isExpired
                        ? 'Payment window expired'
                        : (
                          <>
                            {isUnderpaid ? `Complete your ${paymentTicker} payment` : `Pay with ${paymentTicker}`}
                            <StepTitleMeta>
                              {displayNetworkIcon && <img src={displayNetworkIcon} alt={displayNetworkName} />}
                              {`on ${displayNetworkName}`}
                            </StepTitleMeta>
                          </>
                        )}
                    </StepTitle>
                    {isExpired && (
                      <StepSubtitle>
                        This payment session has expired. Refresh to generate a new payment request.
                      </StepSubtitle>
                    )}
                    {!isExpired && (
                      <StepSubtitle compact>
                        {isUnderpaid
                          ? 'Send the remaining balance to complete your order.'
                          : 'Connect your wallet, scan the QR code, or send the exact amount below.'}
                      </StepSubtitle>
                    )}
                  </StepHeader>

                  {!isExpired && isUnderpaid && (
                    <UnderpaidNotice>
                      <UnderpaidNoticeHeader>Underpaid payment</UnderpaidNoticeHeader>
                      <UnderpaidNoticeBody>
                        <span>
                          We&apos;ve received your payment, but it was under the required amount.
                          Please send the remaining balance to complete your order.
                        </span>
                        <PaymentDetailsBlock>
                          <PaymentDetailRow>
                            <DetailLabel>Amount due</DetailLabel>
                            <DetailValue>{amountDueText} {paymentTicker}</DetailValue>
                          </PaymentDetailRow>
                          <PaymentDetailRow>
                            <DetailLabel>Amount received</DetailLabel>
                            <DetailValue>{confirmedTotalText} {paymentTicker}</DetailValue>
                          </PaymentDetailRow>
                          <PaymentDetailRow>
                            <DetailLabel>Amount remaining</DetailLabel>
                            <DetailValue>{amountRemainingText} {paymentTicker}</DetailValue>
                          </PaymentDetailRow>
                        </PaymentDetailsBlock>
                        {pendingAmount > 0 && (
                          <SectionHelper>
                            We&apos;ve detected another transaction and are waiting for it to confirm.
                            This page updates automatically.
                          </SectionHelper>
                        )}
                      </UnderpaidNoticeBody>
                    </UnderpaidNotice>
                  )}

                  {!isExpired && (
                    <PaymentStepColumn>
                      {showWalletConnectArea && (
                        <PaymentHeroActions style={{ width: '100%', maxWidth: 500, alignSelf: 'center', paddingTop: 4 }}>
                          {!managedDisplayMethod?.isSolana && (
                            <WalletConnectSection>
                              {isConnected && (
                                <ConnectedWalletContainer>
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      border: '1px solid #d3dbea',
                                      borderRadius: 12
                                    }}>
                                    <div style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      padding: 12,
                                      backgroundColor: '#f3f6fc',
                                      borderTopLeftRadius: 12,
                                      borderTopRightRadius: 12
                                    }}>
                                      <span style={{ fontSize: 13, color: '#4f5f7b' }}>Connected wallet</span>
                                      <SecondaryButton
                                        style={{
                                          border: 0,
                                          padding: 0,
                                          color: '#4f5f7b',
                                          fontSize: 11,
                                          textTransform: 'uppercase'
                                        }}
                                        onClick={() => disconnect()}>
                                        Disconnect
                                      </SecondaryButton>
                                    </div>
                                    <span style={{ padding: 12, fontSize: 14 }}>{address}</span>
                                  </div>
                                </ConnectedWalletContainer>
                              )}
                              <PrimaryButton
                                isLoading={isManagedEVMTxLoading}
                                style={{
                                  ...primaryButtonStyle,
                                  minHeight: 54
                                }}
                                onClick={() => { void onClickOpenWallet() }}
                              >
                                {isConnected ? 'Pay with connected wallet' : 'Connect wallet to pay'}
                              </PrimaryButton>
                            </WalletConnectSection>
                          )}

                          {managedDisplayMethod?.isSolana && amountLeftPaymentDetails && (
                            <WalletConnectSection>
                              <SolanaWallet
                                managedPaymentDetails={amountLeftPaymentDetails}
                                isLoading={isManagedEVMTxLoading}
                                setIsLoading={setIsManagedEVMTxLoading}
                                primaryButtonColor={props.checkoutData.customizations?.primaryButtonColor}
                              />
                            </WalletConnectSection>
                          )}
                        </PaymentHeroActions>
                      )}

                      <PaymentFlowDivider>or pay manually</PaymentFlowDivider>

                      {canDisplayQr && isMobilePaymentViewport && (
                        <QrDisclosureButton
                          expanded={showMobileQr}
                          onClick={() => setShowMobileQr((current) => !current)}
                        >
                          <Chevron />
                          <span>{showMobileQr ? 'Hide QR code' : 'Display QR code'}</span>
                        </QrDisclosureButton>
                      )}

                      <ManualPaymentCard>
                        <ManualPaymentGrid hasQr={shouldShowQr}>
                          {shouldShowQr && (
                            <QrHero
                              clickable={!isMobilePaymentViewport}
                              onClick={() => {
                                if (!isMobilePaymentViewport) {
                                  setIsDesktopQrModalVisible(true)
                                }
                              }}
                              onKeyDown={(e) => {
                                if (!isMobilePaymentViewport && (e.key === 'Enter' || e.key === ' ')) {
                                  e.preventDefault()
                                  setIsDesktopQrModalVisible(true)
                                }
                              }}
                              type='button'
                              aria-label={isMobilePaymentViewport ? undefined : 'Open larger QR code'}
                            >
                              <QrHeroVisual>
                                <QrHeroImage src={managedPaymentQrCode} />
                                {!isMobilePaymentViewport && (
                                  <QrHeroHint>
                                    <LinkExternal />
                                    <span>Enlarge</span>
                                  </QrHeroHint>
                                )}
                              </QrHeroVisual>
                            </QrHero>
                          )}

                          <ManualPaymentInfoColumn>
                            <HeroAmountSection>
                              <HeroAmountHeader>
                                <HeroAmountLabel>{isUnderpaid ? 'Amount remaining' : 'Amount due'}</HeroAmountLabel>
                                <CopyButton
                                  onClick={async () => { void navigator.clipboard.writeText(amountLeft) }}
                                  style={{ padding: '3px 6px', fontSize: 10 }}
                                />
                              </HeroAmountHeader>
                              <HeroAmount>
                                <HeroAmountValue>{amountLeftText}</HeroAmountValue>
                                <HeroAmountTicker>{paymentTicker}</HeroAmountTicker>
                              </HeroAmount>
                            </HeroAmountSection>
                          </ManualPaymentInfoColumn>
                        </ManualPaymentGrid>

                        <ManualInfoDivider />

                        <ManualPaymentAddressSection>
                          <DetailLabel style={{ textTransform: 'uppercase', letterSpacing: '0.02em', fontWeight: 600 }}>
                            Payment address
                          </DetailLabel>
                          <PaymentAddressField>
                            <PaymentAddressValue>{formatPaymentAddressForDisplay(paymentAddress)}</PaymentAddressValue>
                            <CopyButton
                              onClick={async () => { void navigator.clipboard.writeText(paymentAddress) }}
                              style={{
                                position: 'absolute',
                                top: 10,
                                right: 10,
                                padding: '4px 8px',
                                fontSize: 10
                              }}
                            />
                          </PaymentAddressField>
                        </ManualPaymentAddressSection>

                        <ManualPaymentFooter>
                          <ManualNoticeIcon />
                          <span>{`Send the exact amount on ${displayNetworkName} only.`}</span>
                        </ManualPaymentFooter>
                      </ManualPaymentCard>

                      {!isMobilePaymentViewport && !!managedPaymentQrCode && (
                        <Modal
                          visible={isDesktopQrModalVisible}
                          onClose={() => setIsDesktopQrModalVisible(false)}
                          title='Scan to pay'
                          innerStyles={{
                            width: 460,
                            maxWidth: '92%',
                            borderRadius: 18,
                            overflow: 'hidden',
                            boxShadow: '0 28px 72px rgba(15, 23, 42, 0.18)',
                            background: 'linear-gradient(180deg, #f7fbff 0%, #ffffff 42%)'
                          }}
                        >
                          <QrModalBody>
                            <QrModalGlow aria-hidden />
                            <QrModalDetails>
                              <QrModalMetaPill>
                                {displayNetworkIcon && <img src={displayNetworkIcon} alt={displayNetworkName} />}
                                <span>{`${paymentTicker} on ${displayNetworkName}`}</span>
                              </QrModalMetaPill>
                              <QrModalAmountBlock>
                                <QrModalAmountLabel>{isUnderpaid ? 'Amount remaining' : 'Amount due'}</QrModalAmountLabel>
                                <QrModalAmount>
                                  <span>{amountLeftText}</span>
                                  <QrModalAmountTicker>{paymentTicker}</QrModalAmountTicker>
                                </QrModalAmount>
                              </QrModalAmountBlock>
                            </QrModalDetails>
                            <QrModalFrame>
                              <QrModalImage src={managedPaymentQrCode} />
                            </QrModalFrame>
                            <QrModalCaption>
                              Scan this code with your wallet app to prefill the payment address and exact amount.
                            </QrModalCaption>
                          </QrModalBody>
                        </Modal>
                      )}

                    </PaymentStepColumn>
                  )}

                  <CTASection>
                    {!isExpired && canChangePaymentMethod && (
                      <SecondaryButton
                        style={secondaryButtonStyle}
                        onClick={resetManagedPaymentSelection}
                      >
                        Change payment method
                      </SecondaryButton>
                    )}

                    {isExpired && (
                      <PrimaryButton
                        style={primaryButtonStyle}
                        onClick={() => window.location.reload()}>
                        Reload payment
                      </PrimaryButton>
                    )}
                  </CTASection>
                </>
              )}
            </StepShell>
          </Container>
        </Wrapper>
      )
    }
  }

  // -------------------------------------------------------------------------------------------------------------------
  return <Wrapper disableAnimation={props.disableAnimation}>
    {props.isSuccess && props.successElement}
    {trfWidget ?? <>

      {
        !props.isSuccess && props.checkoutData.gateway.managed && <Container disableAnimation={props.disableAnimation}>

          {
            // -----------------------------------------------------------------------------------------------------------
            !props.managedPaymentDetails &&
            managedSubscriptionState === undefined &&
            <Container disableAnimation={props.disableAnimation}>
              {
                props.checkoutData.inputFields && props.checkoutData.inputFields.length > 0 &&
                !props.isSuccess &&
                <div
                  style={{
                    width: '100%',
                    display: 'grid',
                    gap: 20,
                    paddingBottom: 25,
                    borderBottom: `1px solid ${RADOM_COLORS.GRAY_MED}`
                  }}>
                  {
                    // ---------------------------------------------------------------------------------------------------
                    // Render zero or more custom input fields
                    localInputFields.map((c, i) => {
                      const validationError = inputFieldValidationAttempted
                        ? inputFieldValidationError(c)
                        : undefined
                      const hasValidationError = Boolean(validationError)

                      return <div
                        key={i}
                        data-input-field-index={i}
                        style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <InputLabel>{c.inputLabel === '' ? 'Input label' : c.inputLabel}</InputLabel>
                          {!c.isRequired && <Cookie>Optional</Cookie>}
                        </div>
                        <div>
                          {
                            // Matching travel-rule values may seed blank custom fields, but customers can still edit them.
                            c.dataType === 'Email' &&
                            <TextInput
                              disabled={props.isPreProcessingPayment}
                              ref={el => inputRefs.current[i] = el}
                              placeholder={`Enter ${c.inputLabel.toLowerCase()}`}
                              value={c.value as string}
                              required={c.isRequired}
                              aria-invalid={hasValidationError}
                              style={inputValidationStyle(hasValidationError, {})}
                              onInput={e => {
                                const newInputs = localInputFields.slice()
                                newInputs[i].value = (e.target as HTMLInputElement).value.toLowerCase()
                                setLocalInputFields(newInputs)
                                props.onInputFieldsChange?.(newInputs)
                              }}
                            />
                          }
                          {
                            // Matching travel-rule values may seed blank custom fields, but customers can still edit them.
                            c.dataType === 'String' &&
                            <TextInput
                              disabled={props.isPreProcessingPayment}
                              ref={el => inputRefs.current[i] = el}
                              placeholder={`Enter ${c.inputLabel.toLowerCase()}`}
                              value={c.value as string}
                              required={c.isRequired}
                              aria-invalid={hasValidationError}
                              style={inputValidationStyle(hasValidationError, {})}
                              onInput={e => {
                                const newInputs = localInputFields.slice()
                                newInputs[i].value = (e.target as HTMLInputElement).value
                                setLocalInputFields(newInputs)
                                props.onInputFieldsChange?.(newInputs)
                              }}
                            />
                          }
                          {
                            c.dataType === 'Number' &&
                            <TextInput
                              disabled={props.isPreProcessingPayment}
                              type="number"
                              value={c.value as number}
                              ref={el => inputRefs.current[i] = el}
                              required={c.isRequired}
                              placeholder="Enter a number"
                              aria-invalid={hasValidationError}
                              style={inputValidationStyle(hasValidationError, {})}
                              onInput={e => {
                                const val = (e.target as HTMLInputElement).value
                                const newInputs = localInputFields.slice()
                                newInputs[i].value = val
                                setLocalInputFields(newInputs)
                                props.onInputFieldsChange?.(newInputs)
                              }}
                            />
                          }
                        </div>
                        {validationError && <InlineInputError>{validationError}</InlineInputError>}
                      </div>
                    })
                  }
                </div>
              }
              <>
                <style>{`
                  @keyframes fadeSlideIn {
                    from {opacity:0;transform:translateY(6px)}
                    to {opacity:1;transform:none}
                  }
                  .pm-list {
                    animation:fadeSlideIn .15s ease-out;
                    overflow: hidden;
                  }

                  @keyframes fadeSlideUp {
                    from {opacity: 0; transform: translateY(10px);}
                    to {opacity: 1; transform: none;}
                  }

                  .pm-sublist.fade-in {
                    animation: fadeSlideUp 0.35s ease-out;
                  }

                  .pm-row {
                    position:relative;
                    border-radius:10px;
                    transition:background-color .18s ease,border-color .18s ease;
                    border: 1px solid transparent;
                    overflow: hidden;
                  }
                  .pm-row:hover {
                    background:#f8fafd;
                    border-color:#e8edf6;
                    overflow: hidden;
                  }
                  .pm-row:focus-visible {
                    outline: 2px solid ${RADOM_COLORS.BLUE5};
                    outline-offset: -2px;
                  }

                  @keyframes fadeRowUp {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: none; }
                  }

                  .pm-row.fade-in-row {}

                  .chevron {
                    transform:rotate(270deg);
                    transition:transform .35s cubic-bezier(.45,.05,.25,1),fill .35s cubic-bezier(.45,.05,.25,1);
                  }
                `}</style>

                {
                  isLoading && (
                    <div
                      style={{
                        width: '15px',
                        height: '15px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        margin: '0 auto'
                      }}
                    >
                      <LoaderWrapper />
                    </div>
                  )
                }

                {
                  !isLoading && <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 10 }}>
                    {
                      !selectedMethod && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <InputLabel style={{
                          textAlign: 'left',
                          margin: '0',
                          fontSize: 14,
                          paddingBottom: 10,
                          color: '#4f5c70',
                          opacity: 1,
                          fontWeight: 600
                        }}>
                          {
                            selectedTicker &&
                          <Chevron className="chevron"
                            onClick={() => {
                              setSelectedTicker('')
                              setSelectedChainName('')
                              setSelectedMethod(undefined)
                            }}
                            fill={RADOM_COLORS.GRAY_DARKEST}
                            style={{
                              width: 14,
                              height: 14,
                              transform: 'rotate(90deg)',
                              cursor: 'pointer',
                              marginRight: 5
                            }} />
                          }

                          {
                            !selectedTicker ? 'Select payment currency' : 'Select Payment Network for'
                          }
                          {
                            selectedTicker &&
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <img src={selectedTokenImageUrl ?? ''} style={{
                              height: 25,
                              width: 25,
                              borderRadius: 10,
                              opacity: 1
                            }} />
                            <span style={{ textTransform: 'capitalize', color: 'black' }}>{selectedTicker}</span>
                          </div>
                          }
                        </InputLabel>
                      </div>
                    }

                    <div className="pm-list" style={{
                      border: '1px solid',
                      borderColor: '#dbe2ef',
                      borderBottom: 'none',
                      borderRadius: 12,
                      maxHeight: 340,
                      overflow: 'hidden',
                      boxShadow: '0 8px 18px rgba(15, 23, 42, 0.06)'
                    }}>
                      {props.checkoutData.gateway.managed.methods.filter(m => !isDelistedMethod(m.network, m.token)).length === 0 && (
                        <div style={{ fontSize: 14, color: RADOM_COLORS.GRAY_DARKER, margin: 20 }}>
                        No available payment methods
                        </div>
                      )}
                      {!selectedTicker && (
                        <>
                          {Array.from(
                            new Map(
                              props.checkoutData.gateway.managed.methods.filter(m => !isDelistedMethod(m.network, m.token)).map(m => {
                                const mm = getMethod(m.network, m.token)
                                if (mm.ticker === 'ETH') {
                                  if (mm.tokenInfo) {
                                    mm.tokenInfo.logo = EthLogo
                                  } else {
                                    mm.logo = EthLogo
                                  }
                                }

                                let discountAmount = 0

                                if (m.discountPercentOff) {
                                  discountAmount = m.discountPercentOff
                                } else if (props.discount) {
                                  const model = props.discount.model
                                  if (model.modelType === 'percentage') {
                                    discountAmount = Number(model.amountOff) / 100
                                  } else if (model.modelType === 'fixed') {
                                    const fixedAmount = Number(model.amountOff)
                                    const converted = convertValue(
                                      fixedAmount,
                                      props.currency,
                                      mm.ticker,
                                      priceQuotes,
                                      false
                                    )
                                    discountAmount = converted / convertValue(
                                      totalSum,
                                      props.currency,
                                      mm.ticker,
                                      priceQuotes,
                                      false
                                    )
                                    discountAmount = Math.min(discountAmount, 1)
                                  }
                                }

                                return [mm.ticker, { method: mm, discountAmount }]
                              })
                            ).values()
                          ).map(({ method, discountAmount }, i, arr) => {
                            const isSelected = selectedTicker === method.ticker
                            const isLast = arr.length >= 3 && i === arr.length - 1
                            const selectTicker = (): void => {
                              if (!validateRequiredInputFields()) {
                                return
                              }

                              setInputFieldsConfirmed(true)
                              const willSelect = !isSelected
                              setSelectedTicker(willSelect ? method.ticker : '')
                              setSelectedTokenImageUrl(method.logo || '')
                              setSelectedChainName('')
                              setSelectedMethod(undefined)
                            }

                            return (
                              <div
                                key={i}
                                className={`pm-row ${isLast ? 'fade-in-row' : ''}`}
                                data-selected={isSelected}
                                role='button'
                                tabIndex={0}
                                aria-label={`Select ${method.ticker} payment method`}
                                style={{
                                  padding: 13,
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  borderBottom: '1px solid #edf1f7',
                                  cursor: 'pointer'
                                }}
                                onClick={selectTicker}
                                onKeyDown={(e) => onKeyboardSelect(e, selectTicker)}
                              >
                                <div style={{ display: 'flex', gap: 15 }}>
                                  <div
                                    style={{
                                      width: 30,
                                      height: 30,
                                      borderRadius: 15,
                                      background: `center/contain no-repeat url(${method.logo || ''})`
                                    }}
                                  />
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                                      {(() => {
                                        const hasQuotes = !!priceQuotes && priceQuotes.length > 0 &&
                                        !isLoadingPriceQuotes
                                        const baseVal = hasQuotes
                                          ? convertValue(
                                            totalSum,
                                            props.currency,
                                            method.ticker,
                                            priceQuotes,
                                            props.checkoutData.chargeCustomerNetworkFee
                                          )
                                          : null

                                        const discountedVal = hasQuotes
                                          ? convertValue(
                                            totalSum,
                                            props.currency,
                                            method.ticker,
                                            priceQuotes,
                                            props.checkoutData.chargeCustomerNetworkFee,
                                            discountAmount
                                          )
                                          : null

                                        return (
                                          <>
                                            {discountAmount > 0 && (
                                              <span style={{ textDecoration: 'line-through', opacity: 0.5 }}>
                                                {hasQuotes ? formatCrypto8(baseVal as number) : '—'}
                                              </span>
                                            )}

                                            <span>
                                              {hasQuotes
                                                ? formatCrypto8(discountedVal as number)
                                                : (isLoadingPriceQuotes ? <Spinner /> : '—')}
                                            </span>
                                          </>
                                        )
                                      })()}
                                      {discountAmount > 0 && (
                                        <span
                                          style={{
                                            padding: '2px 6px',
                                            fontSize: 11,
                                            fontWeight: 600,
                                            background: '#e9f8ef',
                                            border: '1px solid #bce6ca',
                                            borderRadius: 999,
                                            textTransform: 'uppercase',
                                            color: '#256c44'
                                          }}
                                        >
                                          {props.discount && props.discount.model.modelType === 'percentage'
                                            ? `${Number(props.discount.model.amountOff)}% off`
                                            : props.discount
                                              ? `-${Number(props.discount.model.amountOff)} ${props.currency}`
                                              : ''}
                                        </span>
                                      )}
                                    </div>
                                    <div style={{ color: RADOM_COLORS.INPUT_PLACEHOLDER, fontSize: 12 }}>
                                      {method.ticker}
                                    </div>
                                  </div>
                                </div>
                                <Chevron
                                  className="chevron"
                                  fill={isSelected ? RADOM_COLORS.GRAY_DARKER : RADOM_COLORS.GRAY_DARK}
                                  style={{
                                    width: 15,
                                    height: 15,
                                    transform: isSelected ? 'rotate(0deg)' : 'rotate(270deg)'
                                  }}
                                />
                              </div>
                            )
                          })}
                        </>
                      )}

                      <div className={'pm-sublist'}>
                        {selectedTicker && !selectedMethod && Array.from(
                          new Map(
                            props.checkoutData.gateway.managed.methods
                              .filter(m => !isDelistedMethod(m.network, m.token) && getMethod(m.network, m.token).ticker === selectedTicker)
                              .map(m => {
                                const mm = getMethod(m.network, m.token)
                                const uniqueKey = `${m.network}-${m.token ?? 'null'}`
                                return [uniqueKey, { method: mm }]
                              })
                          ).values()
                        ).map(({ method }, i) => {
                          const networkName = method.tokenInfo?.networkName ?? method.name
                          const isSelected = selectedChainName === networkName
                          const selectNetwork = async (): Promise<void> => {
                            if (isLoading || isSelected) return
                            if (!validateRequiredInputFields()) return
                            setInputFieldsConfirmed(true)
                            await proceedWithManagedMethod(method)
                          }

                          return (
                            <div key={i}
                              className="pm-row"
                              data-selected={isSelected}
                              role='button'
                              tabIndex={isLoading ? -1 : 0}
                              aria-disabled={isLoading || isSelected}
                              aria-label={`Pay with ${method.ticker} on ${networkName}`}
                              style={{
                                padding: 13,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                borderBottom: '1px solid #edf1f7',
                                cursor: `${(isSelected || isLoading) ? 'not-allowed' : 'pointer'}`,
                                pointerEvents: isLoading ? 'none' : 'auto'
                              }}
                              onClick={() => { void selectNetwork() }}
                              onKeyDown={(e) => onKeyboardSelect(e, () => { void selectNetwork() })}
                            >
                              <div style={{ display: 'flex', gap: 15 }}>
                                <PaymentMethodIcon method={method} size={30} />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  <div style={{ fontSize: 14, fontWeight: 500, textTransform: 'capitalize' }}>
                                    {networkName}
                                  </div>
                                  <div style={{ color: RADOM_COLORS.INPUT_PLACEHOLDER, fontSize: 12 }}>
                                    {method.ticker}
                                  </div>
                                </div>
                              </div>
                              <Chevron className="chevron" fill={isSelected ? RADOM_COLORS.GRAY_DARKER : RADOM_COLORS.GRAY_DARK} style={{ width: 15, height: 15 }} />
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                }
              </>
            </Container>
          }

          {
            // -----------------------------------------------------------------------------------------------------------
            !props.managedPaymentDetails &&
            managedSubscriptionState === SubscriptionCheckoutState.Select &&
            <Container>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <span>Payment method</span>
                {
                  selectedMethod &&
                  <div style={{
                    display: 'flex',
                    gap: 15,
                    alignItems: 'center'
                  }}>
                    <div style={{
                      width: 30,
                      height: 30,
                      backgroundImage: `url(${selectedMethod.logo})`,
                      backgroundSize: 'contain',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      position: 'relative'
                    }}>
                      {
                        selectedMethod.tokenInfo && <div style={{
                          background: 'rgba(255, 255, 255, 0.95)',
                          borderRadius: '100%',
                          width: 15,
                          height: 15,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'absolute',
                          bottom: -1,
                          right: -5,
                          boxShadow: '0 0 1px'
                        }}>
                          <div style={{
                            width: 10,
                            height: 10,
                            backgroundImage: `url(${selectedMethod.tokenInfo.logo})`,
                            backgroundSize: 'contain',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                          }} />
                        </div>
                      }
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>
                        {selectedMethod.ticker}
                      </span>
                      <span style={{ color: RADOM_COLORS.GRAY_DARKEST }}>
                        {selectedMethod.tokenInfo?.networkName ?? selectedMethod.name}
                      </span>
                    </div>
                  </div>
                }
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>

                <span>Select subscription type</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <PrimaryButton
                    disabled={props.isPreProcessingPayment || isLoadingAllowance || !isAutopayEnabled(selectedMethod)}
                    style={{
                      background: 'none',
                      color: RADOM_COLORS.BLACK,
                      justifyContent: 'flex-start',
                      border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                      padding: 20,
                      borderRadius: 5,
                      fontSize: 14
                    }}
                    onClick={() => {
                      setSubscriptionValidationError('')
                      setSelectedSubscriptionType(SubscriptionType.Autopay)

                      const { emailAddress } = travelRuleFields
                      const { travelRuleEmail } = props.checkoutData
                      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                      const trfEmail = emailAddress || travelRuleEmail || ''

                      setSubscriptionEmailAddress(trfEmail)
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between', width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Checkbox isRadio checked={selectedSubscriptionType === SubscriptionType.Autopay} />
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                          <span style={{ textAlign: 'left' }}>Subscribe with auto renew</span>
                          <span style={{ opacity: 0.5, textAlign: 'left' }}>Set an alllowance and automatically pay</span>
                        </div>
                      </div>
                    </div>
                  </PrimaryButton>

                  <PrimaryButton
                    disabled={props.isPreProcessingPayment || isLoadingAllowance}
                    style={{
                      background: 'none',
                      color: RADOM_COLORS.BLACK,
                      justifyContent: 'flex-start',
                      border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                      padding: 20,
                      borderRadius: 5,
                      fontSize: 14
                    }}
                    onClick={() => {
                      setSubscriptionValidationError('')
                      setSelectedSubscriptionType(SubscriptionType.EmailInvoice)
                      // Fill the subscription invoice email address with the travel rule fields email address
                      // If the email address has not been collected as part of the travel rule fields, then its because
                      // we already have that value from a previous checkout and therefore it has been supplied by the
                      // backend
                      const { emailAddress } = travelRuleFields
                      const { travelRuleEmail } = props.checkoutData
                      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                      const trfEmail = emailAddress || travelRuleEmail || ''

                      setSubscriptionEmailAddress(trfEmail)
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between', width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Checkbox isRadio checked={selectedSubscriptionType === SubscriptionType.EmailInvoice} />
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                          <span style={{ textAlign: 'left' }}>Subscribe with email invoices</span>
                          <span style={{ opacity: 0.5, textAlign: 'left' }}>Manually pay using email invoices</span>
                        </div>
                      </div>
                    </div>
                  </PrimaryButton>
                </div>
                {selectedSubscriptionType === undefined && subscriptionValidationError && (
                  <InlineInputError>{subscriptionValidationError}</InlineInputError>
                )}

                {
                  selectedSubscriptionType === SubscriptionType.EmailInvoice &&
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <span>Email address</span>
                    <TextInput
                      autoFocus
                      placeholder='Enter email address'
                      value={subscriptionEmailAddress}
                      aria-invalid={Boolean(subscriptionValidationError)}
                      style={inputValidationStyle(Boolean(subscriptionValidationError), {})}
                      onChange={e => {
                        setSubscriptionValidationError('')
                        setSubscriptionEmailAddress(e.target.value.toLowerCase())
                      }} />
                    {subscriptionValidationError && <InlineInputError>{subscriptionValidationError}</InlineInputError>}
                  </div>
                }

                {
                  selectedSubscriptionType === SubscriptionType.Autopay &&
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}
                      onClick={() => setReceiveEmailNotifications(!receiveEmailNotifications)}>
                      <Checkbox checked={receiveEmailNotifications} />
                      <span>Send me email notifications about my subscription</span>
                    </div>

                    {
                      receiveEmailNotifications &&
                      <TextInput
                        autoFocus
                        placeholder='Enter email address'
                        value={subscriptionEmailAddress}
                        aria-invalid={Boolean(subscriptionValidationError)}
                        style={inputValidationStyle(Boolean(subscriptionValidationError), {})}
                        onChange={e => {
                          setSubscriptionValidationError('')
                          setSubscriptionEmailAddress(e.target.value.toLowerCase())
                        }} />
                    }
                    {subscriptionValidationError && <InlineInputError>{subscriptionValidationError}</InlineInputError>}
                  </div>
                }
              </div>

              {
                selectedSubscriptionType === SubscriptionType.Autopay && address &&
                <ConnectedWalletContainer>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                      borderRadius: 5
                    }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: 15,
                      backgroundColor: RADOM_COLORS.GRAY_MED
                    }}>
                      <span style={{ fontSize: 14 }}>Connected wallet</span>
                      <SecondaryButton
                        style={{
                          border: 0,
                          padding: 0,
                          color: RADOM_COLORS.BLACK,
                          fontSize: 10,
                          textTransform: 'uppercase'
                        }}
                        onClick={() => disconnect()}>
                        Disconnect
                      </SecondaryButton>
                    </div>
                    <span style={{ padding: 15 }}>{address}</span>
                  </div>
                </ConnectedWalletContainer>
              }

              <PrimaryButton
                isLoading={
                  isLoadingPriceQuotes ||
                  isSettingAllowance ||
                  props.isPreProcessingPayment ||
                  isLoadingAllowance
                }
                disabled={
                  isLoadingPriceQuotes ||
                  isSettingAllowance ||
                  props.isPreProcessingPayment ||
                  isLoadingAllowance
                }
                style={{ background: props.checkoutData.customizations?.primaryButtonColor, color: getContrastColor(props.checkoutData.customizations?.primaryButtonColor), width: '100%', margin: 'auto' }}
                onClick={async () => {
                  if (!validateSubscriptionStep()) {
                    return
                  }

                  if (selectedSubscriptionType === SubscriptionType.Autopay && !isConnected) {
                    return await open()
                  }

                  if (!selectedMethod || selectedSubscriptionType === undefined) {
                    return
                  }

                  props.onCheckout({
                    managed: {
                      selectedMethod
                    },
                    priceQuotes,
                    subscriptionOptions: {
                      autopay: selectedSubscriptionType === SubscriptionType.Autopay
                        ? {
                          subscriptionType: selectedSubscriptionType,
                          address: address as any
                        }
                        : undefined,
                      emailInvoice: selectedSubscriptionType === SubscriptionType.EmailInvoice
                        ? {
                          subscriptionType: selectedSubscriptionType,
                          emailAddress: subscriptionEmailAddress.toLowerCase()
                        }
                        : undefined
                    },
                    travelRuleFields
                  })
                }}>
                {
                  selectedSubscriptionType === SubscriptionType.Autopay && !isConnected
                    ? 'Approve payment'
                    : 'Continue'
                }
              </PrimaryButton>

              <SecondaryButton
                disabled={isLoadingPriceQuotes || isSettingAllowance ||
                  isLoadingAllowance || props.isPreProcessingPayment}
                style={{ border: 0, padding: 5, color: RADOM_COLORS.GRAY_DARKER }}
                onClick={resetManagedPaymentSelection}>
                Change payment method
              </SecondaryButton>
            </Container>
          }

          {
            // -----------------------------------------------------------------------------------------------------------
            !props.managedPaymentDetails &&
            (managedSubscriptionState === SubscriptionCheckoutState.Allowance ||
              managedSubscriptionState === SubscriptionCheckoutState.Confirm) &&
            selectedMethod &&
            <Container style={{ gap: 30 }}>
              <div style={{
                display: 'flex',
                userSelect: 'none',
                alignItems: 'center',
                gap: 10,
                padding: 15,
                borderRadius: 5,
                border: `1px solid ${RADOM_COLORS.GRAY_MED}`
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  flexShrink: 0,
                  padding: '0 20px'
                }}>
                  <span style={{
                    display: 'flex',
                    borderRadius: '100%',
                    width: 20,
                    height: 20,
                    backgroundColor: managedSubscriptionState === SubscriptionCheckoutState.Confirm
                      ? RADOM_COLORS.SUCCESS_LIGHT
                      : RADOM_COLORS.NICE_GREEN,
                    fontSize: 12,
                    fontWeight: 500,
                    color: 'white',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: '0.2s ease all'
                  }}>1</span>
                  <span style={{
                    fontSize: 16,
                    fontWeight: managedSubscriptionState === SubscriptionCheckoutState.Confirm ? 300 : 400,
                    color: managedSubscriptionState === SubscriptionCheckoutState.Confirm
                      ? RADOM_COLORS.GRAY_DARKEST
                      : RADOM_COLORS.BLACK,
                    transition: '0.2s ease all'
                  }}>Set allowance</span>
                </div>

                <div style={{
                  width: '100%',
                  backgroundColor: RADOM_COLORS.GRAY_DARK,
                  height: 2,
                  borderRadius: 2,
                  flexGrow: 1,
                  transition: '0.2s ease all'
                }} />
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  flexShrink: 0,
                  padding: '0 20px'
                }}>
                  <span style={{
                    display: 'flex',
                    borderRadius: '100%',
                    width: 20,
                    height: 20,
                    backgroundColor: managedSubscriptionState === SubscriptionCheckoutState.Confirm
                      ? RADOM_COLORS.NICE_GREEN
                      : RADOM_COLORS.GRAY_DARKER,
                    fontSize: 12,
                    fontWeight: 500,
                    color: 'white',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: '0.2s ease all'
                  }}>2</span>
                  <span style={{
                    fontSize: 16,
                    fontWeight: managedSubscriptionState === SubscriptionCheckoutState.Confirm ? 400 : 300
                  }}>Subscribe</span>

                </div>
              </div>

              {
                managedSubscriptionState === SubscriptionCheckoutState.Allowance && <>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                    color: RADOM_COLORS.BLACK,
                    borderRadius: 5
                  }}>
                    <span>Set an allowance to enable automatically recurring payments for your subscription.</span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <PrimaryButton
                      isLoading={isLoadingPriceQuotes || isSettingAllowance}
                      style={{
                        background: props.checkoutData.customizations?.primaryButtonColor,
                        width: '100%',
                        margin: 'auto'
                      }}
                      onClick={() => {
                        setManagedSubscriptionAllowance()
                      }}>
                      Set allowance
                    </PrimaryButton>

                    {
                      allowSkipAllowance &&
                      <SecondaryButton
                        disabled={isLoadingPriceQuotes || isSettingAllowance}
                        style={{
                          padding: 5,
                          color: RADOM_COLORS.GRAY_DARKER,
                          borderColor: RADOM_COLORS.GRAY_DARK
                        }}
                        onClick={() => setManagedSubscriptionState(SubscriptionCheckoutState.Confirm)}>
                        Skip
                      </SecondaryButton>
                    }

                    <SecondaryButton
                      disabled={isLoadingPriceQuotes || isSettingAllowance}
                      style={{ border: 0, padding: 5, color: RADOM_COLORS.GRAY_DARKER }}
                      onClick={resetManagedPaymentSelection}>
                      Change payment method
                    </SecondaryButton>
                  </div>
                </>
              }

              {
                managedSubscriptionState === SubscriptionCheckoutState.Confirm &&
                <Container>
                  <div style={{ boxShadow: `0 0 3px ${RADOM_COLORS.GRAY_DARK}`, borderRadius: 5 }}>
                    <div style={{
                      backgroundColor: RADOM_COLORS.GRAY_MED,
                      border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                      borderTopLeftRadius: 5,
                      borderTopRightRadius: 5,
                      padding: 10
                    }}>Initial payment</div>
                    <div style={{
                      display: 'flex',
                      gap: 15,
                      alignItems: 'center',
                      border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                      padding: 15,
                      borderTop: 0,
                      borderBottomLeftRadius: 5,
                      borderBottomRightRadius: 5
                    }}>
                      <div style={{
                        width: 30,
                        height: 30,
                        backgroundImage: `url(${selectedMethod.logo})`,
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        position: 'relative'
                      }}>
                        {
                          selectedMethod.tokenInfo && <div style={{
                            background: 'rgba(255, 255, 255, 0.95)',
                            borderRadius: '100%',
                            width: 15,
                            height: 15,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'absolute',
                            bottom: -1,
                            right: -5,
                            boxShadow: '0 0 1px'
                          }}>
                            <div style={{
                              width: 10,
                              height: 10,
                              backgroundImage: `url(${selectedMethod.tokenInfo.logo})`,
                              backgroundSize: 'contain',
                              backgroundPosition: 'center',
                              backgroundRepeat: 'no-repeat'
                            }} />
                          </div>
                        }
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>

                          <span style={{ fontSize: 16 }}>
                            {
                              isLoadingPriceQuotes
                                ? <Spinner />
                                : formatCrypto8(
                                  convertValue(totalSum, props.currency, selectedMethod.ticker, priceQuotes, false)
                                )
                            }
                          </span>

                          {
                            props.checkoutData.chargeCustomerNetworkFee &&
                            <>
                              <span>+</span>

                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                backgroundColor: RADOM_COLORS.GRAY_MED,
                                borderRadius: 5,
                                padding: '2px 6px',
                                color: RADOM_COLORS.BLACK
                              }}>
                                <GasPump style={{ height: 10 }} fill={RADOM_COLORS.BLACK} />
                                <span>{gasCost}</span>
                              </div>
                            </>
                          }

                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
                          <span style={{ fontSize: 12, color: RADOM_COLORS.GRAY_DARKER }}>
                            {selectedMethod.ticker}
                          </span>
                          <span style={{ color: RADOM_COLORS.GRAY_DARKEST }}>
                            on {selectedMethod.tokenInfo?.networkName ?? selectedMethod.name}
                          </span>
                        </div>
                      </div>
                    </div>

                  </div>

                  <div style={{ boxShadow: `0 0 3px ${RADOM_COLORS.GRAY_DARK}`, borderRadius: 5 }}>
                    <div style={{
                      border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                      borderBottom: 0,
                      borderTopLeftRadius: 5,
                      borderTopRightRadius: 5,
                      padding: 10,
                      color: RADOM_COLORS.GRAY_DARKEST,
                      backgroundColor: RADOM_COLORS.GRAY_LIGHTEST
                    }}>Recurring payments</div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 20px',
                      color: RADOM_COLORS.GRAY_DARKEST,
                      borderTop: 0,
                      border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                      borderBottomLeftRadius: 5,
                      borderBottomRightRadius: 5
                    }}>
                      <div style={{
                        width: 20,
                        height: 20,
                        backgroundImage: `url(${selectedMethod.logo})`,
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        position: 'relative'
                      }}>
                        {
                          selectedMethod.tokenInfo && <div style={{
                            background: 'rgba(255, 255, 255, 0.95)',
                            borderRadius: '100%',
                            width: 12,
                            height: 12,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'absolute',
                            bottom: -1,
                            right: -5,
                            boxShadow: '0 0 1px'
                          }}>
                            <div style={{
                              width: 8,
                              height: 8,
                              backgroundImage: `url(${selectedMethod.tokenInfo.logo})`,
                              backgroundSize: 'contain',
                              backgroundPosition: 'center',
                              backgroundRepeat: 'no-repeat'
                            }} />
                          </div>
                        }
                      </div>
                      <div style={{ display: 'flex', gap: 3 }}>
                        {formatCrypto8(
                          convertValue(recurringSum, props.currency, selectedMethod.ticker, priceQuotes,
                            false)
                        )} {
                          props.checkoutData.chargeCustomerNetworkFee &&
                          `+ gas fee per ${formatSubscriptionDuration(recurringInterval() * 60 * 60 * 24).toLowerCase()}`
                        }
                        <span>starting</span>
                        {nextBillingAt()}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <PrimaryButton
                      isLoading={
                        isLoadingPriceQuotes || props.isPreProcessingPayment || isSigningMessage
                      }
                      style={{
                        background: props.checkoutData.customizations?.primaryButtonColor,
                        width: '100%',
                        margin: 'auto'
                      }}
                      onClick={async () => {
                        if (!props.tokenDelegatePaymentDetails || !address) {
                          return
                        }

                        setIsSigningMessage(true)

                        try {
                          const chain = NetworkToChainMap[props.tokenDelegatePaymentDetails.network] as any
                          const provider: any = await connector?.getProvider()

                          const signer = createWalletClient({
                            chain,
                            transport: custom(provider)
                          })

                          const signature = await signer?.signMessage({
                            account: address,
                            message: props.tokenDelegatePaymentDetails.messageToSign
                          })

                          props.onCheckout({
                            travelRuleFields,
                            completeTokenDelegate: {
                              paymentId: props.tokenDelegatePaymentDetails.paymentId,
                              signature
                            },
                            priceQuotes
                          })
                        } catch (err) {
                          errorToast(err.toString())
                        }

                        setIsSigningMessage(false)
                      }}>
                      Subscribe
                    </PrimaryButton>

                    <SecondaryButton
                      style={{ border: 0, padding: 5, color: RADOM_COLORS.GRAY_DARKER }}
                      disabled={isLoadingPriceQuotes || props.isPreProcessingPayment || isSigningMessage}
                      onClick={resetManagedPaymentSelection}>
                      Change payment method
                    </SecondaryButton>
                  </div>

                </Container>
              }

            </Container>
          }

          {
            // -----------------------------------------------------------------------------------------------------------
            props.managedPaymentDetails &&
            <Container style={{
              overflowWrap: 'anywhere',
              ...(props.posMode && {
                alignItems: 'center',
                gap: 60,
                overflowWrap: 'normal'
              })
            }}>
              {
                props.posMode &&
                <WaveContainer>
                  <Wave />
                  <Wave2 />
                  <Wave3 />
                </WaveContainer>
              }
              <Styles />
              {
                props.posMode &&
                <div style={{ display: 'flex', flexDirection: 'column', fontSize: 12, gap: 3, alignItems: 'center' }}>
                  <span style={{ opacity: 0.5, textTransform: 'uppercase', textAlign: props.posMode ? 'center' : undefined }}>Time remaining</span>
                  <span style={{ fontSize: 16, fontWeight: 100, textAlign: 'center' }}>
                    {
                      formatSecondsToMinutesAndSecondsString(
                        (new Date(
                          props.paymentStatus?.expiresAt ??
                          props.managedPaymentDetails.expiresAt
                        ).getTime() - new Date().getTime()) / 1000
                      )
                    }
                  </span>
                </div>
              }
              <div>
                <div style={{
                  fontSize: 20,
                  fontWeight: 500,
                  textAlign: 'center',
                  color: RADOM_COLORS.GRAY_DARKER,
                  ...(props.posMode && { marginBottom: 20, marginTop: 10 })
                }}>
                  Pay with <img
                    src={
                      getMethod(props.managedPaymentDetails.network, props.managedPaymentDetails.token).logo
                    }
                    style={{
                      height: 20,
                      width: 'auto',
                      paddingLeft: 5
                    }}
                  /> <span style={{ color: 'black' }}>{getMethod(
                    props.managedPaymentDetails.network,
                    props.managedPaymentDetails.token
                  ).ticker}</span> on
                  <span style={{ color: 'black', marginLeft: 5 }}>{
                    getMethod(
                      props.managedPaymentDetails.network,
                      props.managedPaymentDetails.token
                    )?.tokenInfo?.networkName ?? getMethod(
                      props.managedPaymentDetails.network,
                      props.managedPaymentDetails.token
                    )?.name
                  }</span> network
                </div>
                <div style={{
                  fontSize: 14,
                  textAlign: 'center',
                  color: RADOM_COLORS.GRAY_DARKER,
                  marginTop: 10
                }}>
                  Connect to your wallet, scan the QR code or copy the address to make a payment.
                </div>
              </div>
              {
                props.paymentStatus?.status !== 'expired' &&
                props.checkoutData.customizations?.displayQrCode !== false &&
                <QRCodeWrapper style={{
                  margin: '0 auto'
                }}>
                  <QRCodeWrapperInner>
                    <img src={managedPaymentQrCode} style={{ width: 150, height: 150 }} />
                  </QRCodeWrapperInner>
                </QRCodeWrapper>
              }
              <div style={{
                padding: '10px 15px',
                borderRadius: 20,
                margin: '0 auto',
                backgroundColor: RADOM_COLORS.BLUE7,
                color: RADOM_COLORS.BLUE5
              }}>
                Time Remaining <span style={{ color: RADOM_COLORS.BLUE4, fontWeight: 500 }}>
                  {formatSecondsToMinutesAndSecondsString(
                    (new Date(
                      props.paymentStatus?.expiresAt ??
                      props.managedPaymentDetails.expiresAt
                    ).getTime() - new Date().getTime()) / 1000
                  )}
                </span>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 20,
                ...(props.posMode && { gap: 40 })
              }}>

                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  fontSize: 12,
                  gap: 3,
                  ...(props.posMode && { alignItems: 'center' })
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ opacity: 0.5, textTransform: 'uppercase' }}>Payment address</span>
                    {
                      props.paymentStatus?.status !== 'expired' &&
                      <CopyButton onClick={async () =>
                        await navigator.clipboard.writeText(props.managedPaymentDetails?.paymentAddress ?? '')} />
                    }
                  </div>
                  <span style={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 2, fontWeight: 100 }}>
                    {props.paymentStatus?.status === 'expired'
                      ? 'Expired'
                      : props.managedPaymentDetails.paymentAddress
                    }
                  </span>
                </div>

                <div style={{ display: 'flex', gap: '20px 40px', flexWrap: 'wrap' }}>

                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    fontSize: 12,
                    gap: 3,
                    flexGrow: 1,
                    ...(props.posMode && { alignItems: 'center' })
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ opacity: 0.5, textTransform: 'uppercase' }}>Amount due</span>
                      {
                        props.paymentStatus?.status !== 'expired' &&
                        <CopyButton onClick={async () =>
                          await navigator.clipboard.writeText(formatCrypto8(props.managedPaymentDetails?.amount as any)
                          )}
                        />
                      }
                    </div>

                    <div style={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 5, fontWeight: 300 }}>
                      <img
                        src={getMethod(props.managedPaymentDetails.network, props.managedPaymentDetails.token).logo}
                        style={{ height: 20, width: 'auto' }}
                      />
                      {
                        formatCrypto8(props.managedPaymentDetails.amount as any)
                      }
                      <span style={{ opacity: 0.5, fontWeight: 100 }}>
                        {getMethod(props.managedPaymentDetails.network, props.managedPaymentDetails.token).ticker}
                      </span>
                    </div>
                  </div>
                </div>

                {/* {
                !props.posMode &&
                <div style={{ display: 'flex', flexDirection: 'column', fontSize: 12, gap: 3 }}>
                  <span style={{ opacity: 0.5, textTransform: 'uppercase' }}>Time remaining</span>
                  <span style={{ fontSize: 16, fontWeight: 100 }}>
                    {
                      props.paymentStatus?.status === 'expired'
                        ? 'Expired'
                        : formatSecondsToMinutesAndSecondsString(
                          (
                            new Date(
                              props.paymentStatus?.expiresAt ??
                              props.managedPaymentDetails.expiresAt
                            ).getTime() -
                            new Date().getTime()) /
                          1000
                        )
                    }
                  </span>
                </div>
              } */}

                {
                  props.paymentStatus && props.paymentStatus.transactions.length > 0 &&
                  <div style={{ borderRadius: 15, border: `1px solid ${RADOM_COLORS.GRAY_MED}`, overflow: 'hidden' }}>
                    <div style={{ padding: 15, backgroundColor: 'rgb(255 223 230)' }}>Underpaid payment</div>
                    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>
                      <div>We have received your payment, but it was under the required amount.
                        Please send the remaining balance to complete your order.</div>
                      <div style={{ width: '100%', height: 1, backgroundColor: RADOM_COLORS.GRAY_DARK }} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <span style={{ opacity: 0.5, textTransform: 'uppercase', fontSize: 12 }}>Amount remaining</span>
                        <span>

                          <div style={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 5, fontWeight: 300 }}>
                            <img
                              src={
                                getMethod(
                                  props.managedPaymentDetails.network,
                                  props.managedPaymentDetails.token).logo
                              }
                              style={{ height: 20, width: 'auto' }}
                            />
                            {
                              formatCrypto8(
                                props.paymentStatus.amountRemaining as any)
                            }
                            <span style={{ opacity: 0.5, fontWeight: 100 }}>
                              {getMethod(props.managedPaymentDetails.network, props.managedPaymentDetails.token).ticker}
                            </span>
                          </div>
                        </span>
                      </div>
                    </div>
                  </div>
                }

                {
                  props.paymentStatus &&
                  props.paymentStatus.pendingTransactions.length > 0 &&
                  props.paymentStatus.transactions.length <= 0 &&
                  <PaymentDetectedContainer>
                    <div style={{ padding: 15, backgroundColor: RADOM_COLORS.GRAY_MED }}>Payment detected</div>
                    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>
                      <div>Your payment has been detected and is currently pending confirmation.</div>
                      <div style={{ width: '100%', height: 1, backgroundColor: RADOM_COLORS.GRAY_DARK }} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <span style={{ opacity: 0.5, textTransform: 'uppercase', fontSize: 12 }}>Amount paid</span>
                        <span>

                          <div style={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 5, fontWeight: 300 }}>
                            <img
                              src={
                                getMethod(
                                  props.managedPaymentDetails.network,
                                  props.managedPaymentDetails.token).logo
                              }
                              style={{ height: 20, width: 'auto' }}
                            />
                            {
                              formatCrypto8(
                                props.paymentStatus.pendingTransactions.reduce((s, t) => s + Number(t.amount), 0)
                              )
                            }
                            <span style={{ opacity: 0.5, fontWeight: 100 }}>
                              {getMethod(props.managedPaymentDetails.network, props.managedPaymentDetails.token).ticker}
                            </span>
                          </div>
                        </span>
                      </div>
                    </div>
                  </PaymentDetectedContainer>
                }

                {
                  !props.posMode && props.paymentStatus?.status !== 'expired' &&
                  props.checkoutData.organizationId !== 'f58cf6b3-b87d-4893-b859-cddc85ac0abf' && !isBitcoinNetwork &&
                  <div style={{
                    width: '100%',
                    borderTop: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                    paddingTop: 20,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 20
                  }}>

                    {
                      !selectedMethod?.isSolana && <>
                        {
                          isConnected &&
                          <ConnectedWalletContainer>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                                borderRadius: 5
                              }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: 15,
                                backgroundColor: RADOM_COLORS.GRAY_MED
                              }}>
                                <span style={{ fontSize: 14 }}>Connected wallet</span>
                                <SecondaryButton
                                  style={{
                                    border: 0,
                                    padding: 0,
                                    color: RADOM_COLORS.BLACK,
                                    fontSize: 10,
                                    textTransform: 'uppercase'
                                  }}
                                  onClick={() => disconnect()}>
                                  Disconnect
                                </SecondaryButton>
                              </div>
                              <span style={{ padding: 15 }}>{address}</span>
                            </div>
                          </ConnectedWalletContainer>
                        }
                        <PrimaryButton
                          isLoading={isManagedEVMTxLoading}
                          onClick={onClickOpenWallet}
                          style={{
                            width: '100%',
                            background: props.checkoutData.customizations?.primaryButtonColor,
                            color: getContrastColor(props.checkoutData.customizations?.primaryButtonColor)
                          }}>
                          {
                            isConnected ? 'Approve payment' : 'Connect wallet'
                          }
                        </PrimaryButton>
                      </>
                    }

                    {
                      selectedMethod?.isSolana &&
                      <SolanaWallet
                        managedPaymentDetails={props.managedPaymentDetails}
                        isLoading={isManagedEVMTxLoading}
                        setIsLoading={setIsManagedEVMTxLoading}
                        primaryButtonColor={props.checkoutData.customizations?.primaryButtonColor} />
                    }
                  </div>
                }

                {
                  !props.posMode && props.paymentStatus?.status === 'expired' &&
                  <div style={{
                    width: '100%',
                    borderTop: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
                    paddingTop: 20
                  }}>
                    <PrimaryButton
                      onClick={() => window.location.reload()}
                      style={{
                        width: '100%',
                        background: props.checkoutData.customizations?.primaryButtonColor
                      }}>
                      Reload
                    </PrimaryButton>
                  </div>
                }
              </div>

            </Container>
          }

        </Container>
      }
    </>
    }

  </Wrapper>
}

export default PayWidget
